/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable eqeqeq */
/**
 * Phase 6e2c-3 — ACCESSORY SELECTOR (§33.8 — accessory.selector.js 1,913).
 *
 * ONE shared React view serving damper / louver / hood / opening (legacy: one
 * View class + 4 templates). Ported into the 6e2c-0+1 component-selector chassis
 * (§30) as a tab body. Opened from each accessory FORM's rating label
 * (AccessorySelectHeading → rating_mode:activate {name:type, current_model:{id},
 * section}). Chassis fan-out key: <type>_<accessoryId>:selector:<action>.
 *
 * REUSE (6e2b infrastructure — NOT rebuilt):
 *  - window.__FORM_SHIM__[id] (Q15 shim): createPosition / autoSize /
 *    dataGetterForRendering / defaultFaceVelocity / model / render.
 *  - AccessoryMountingPopup inlineContainer mode (the Mounting tab).
 *  - createAccessoryPreviewCanvas (the AHU mini-3D tab) via
 *    useAccessoryPreviewCanvas.
 *  - mountingMath / MountingModel (the Mounting tab math), via staging adapters.
 *  - the pure data helpers in accessorySelectorData.ts (CMS cascades, smartlist
 *    URLs, mounting image, plane resolution).
 *
 * ⚠ DATA-SAFETY (§4 + 6e2a userArmed gate): NOTHING fires on open / mount /
 * tab-switch (staging is a clone of the existing model → no diff → no PATCH).
 * The with_position round-trip (getAccessoryData) + Select are armed ONLY by a
 * real user field change. In smoke they are CODE-verified only.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import { useGetCmsAhuDocQuery } from 'app/services/ahuBoardApi';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import type { SelectorTabProps } from './selectorTypes';
import type { ComponentFormShim } from '../componentFormTypes';
import { subscribeFormShims } from '../componentFormTypes';
import { StagingModel } from './accessorySelectorStaging';
import {
  accessorySelectorApi,
  DAMPER_CMS_FIELDS,
  CMS_URL_FOR_TYPE,
  unwrapAccessoryCms,
  type WithPositionBody,
} from './accessorySelectorApi';
import {
  parseParams,
  planesOnCabinet,
  resolvePlane,
  mountingPlaneValue,
  getAvailableAirwayDimensions,
  syncManufacturerSeriesFilters,
  resolveMountingImage,
  buildSmartlistUrls,
  resolveDamperCmsUrl,
  type PlaneRow,
} from './accessorySelectorData';
import { useAccessoryPreviewCanvas } from './useAccessoryPreviewCanvas';
import {
  StagingFormAdapter,
  StagingPositionAdapter,
  buildStagingMounting,
} from './accessoryStagingMounting';
import AccessoryMountingPopup from '../accessory/AccessoryMountingPopup';
import { AccessoryLeftPanel, ComboSmartlistRows, SmartlistRows } from './AccessorySelectorBody';
import {
  applyEnclosureDefaults,
  buildComboCanvasFaces,
  comboAutoSizeEnabledFromModel,
  comboIsSelectValid,
  comboLouverCounts,
  comboSmartlistFilteredItems,
  COMBO_DEFAULT_FACE_VELOCITY,
  COMBO_NAME_PARAM,
  emptyComboAwDims,
  getComboFacesView,
  initComboFaces,
  type ComboAwDims,
  type ComboFace,
  type ComboFacesState,
} from './comboLouver';
import { comboLouverApi } from './comboLouverApi';
import { comboNum, createComboEngine, type ComboEngine } from './comboLouverEngine';
import { ComboLouverHowTo } from './ComboLouverHowTo';
import { commitNumberValue } from './selectorNumberInput';

type AccType = 'damper' | 'louver' | 'hood' | 'opening';

/** The right panel's `data-tab` values — legacy keeps this on the DOM node. */
type DataTab = 'ahu' | 'mounting' | 'component' | 'smartlist';

const FACE_VEL_DEFAULT: Record<AccType, number> = {
  damper: 1200,
  louver: 1200,
  hood: 500,
  opening: 1200,
};

/** changeableFields per type (initialize :88-93 — copied verbatim). */
const CHANGEABLE_FIELDS: Record<AccType, string[]> = {
  opening: ['width', 'height', 'airflow_actual', 'mounting_plane_id', 'air_stream_id', 'shape_id'],
  damper: ['width', 'height', 'airflow_actual', 'mounting_plane_id', 'air_stream_id', 'blade_direction_id', 'master_damper_id', 'air_direction_id', 'hood_id', 'louver_id', 'casing_mount_id'],
  louver: ['width', 'height', 'airflow_actual', 'mounting_plane_id', 'air_stream_id', 'blade_direction_id', 'master_louver_id', 'air_direction_id', 'casing_mount_id'],
  hood: ['width', 'height', 'height_2', 'depth', 'style_id', 'depth_mode_id', 'airflow_actual', 'mounting_plane_id', 'air_stream_id', 'mtl_id', 'screen_id', 'allow_fan_intake', 'include_opening_in_vp_calc', 'air_direction_id', 'angle_1', 'angle_2', 'angle_3', 'angle_4', 'airflow_face_id'],
};

/** selectAccessory modelFields (:1101-1110 — the 24-field diff list). */
const MODEL_FIELDS = [
  'tag', 'airflow_actual', 'air_stream_id', 'air_direction_id', 'shape_id',
  'width', 'height', 'height_2', 'casing_mount_id', 'blade_direction_id',
  'style_id', 'depth_mode_id', 'mtl_id', 'screen_id', 'airflow_face_id', 'depth',
  'allow_fan_intake', 'include_opening_in_vp_calc', 'hood_id', 'louver_id',
  'master_damper_id', 'master_louver_id', 'angle_1', 'angle_2', 'angle_3', 'angle_4',
];

const POSITION_FIELDS = ['x', 'y', 'mounting_plane_id', 'mounting_type', 'default_pos_x', 'default_pos_y'];

const PLURAL: Record<AccType, string> = {
  damper: 'dampers',
  louver: 'louvers',
  hood: 'hoods',
  opening: 'openings',
};

const faceVelKey = (id: number) => `desired_face_velocity_actual_${id}`;

/**
 * COMBO LOUVER (KA-5245) — `self.$el.find('input[name="…"]').val()`. Legacy reads
 * `enclosure_depth` and `desired_face_velocity_actual` straight off the DOM in
 * `_comboReady` (:2534-2535), `_fetchComboAwDims` (:1377-1382), `_comboAutoSize`
 * (:2591-2592) and `getAccessoryData` (:3669-3670) rather than off the staging
 * model. Reproduced verbatim: it is what makes "the DOM is transiently incomplete
 * during a render burst" observable, which is the whole reason
 * `_maybeComboAutoSize` re-checks readiness INSIDE its debounce. `#component_
 * rating_mode` is React's dialog host (BoardContent.tsx:497), i.e. `self.$el`.
 */
const dialogInputValue = (name: string): string | null =>
  document.querySelector<HTMLInputElement>(`#component_rating_mode input[name="${name}"]`)?.value ??
  null;

export interface AccessorySelectorProps extends SelectorTabProps {
  type: AccType;
}

/**
 * ★ THE READINESS GATE — do not fold this into the body.
 *
 * The body seeds `staging_position_model` ONCE, from `shim.getPosition()`
 * (legacy `new Backbone.Model(current_model.position.toJSON())`, :112). The live
 * position is a SECOND round trip, fired by the host form after its detail GET —
 * i.e. strictly after the shim lands in `window.__FORM_SHIM__` — so a selector
 * that mounts in the same breath as its form loses that race every time.
 *
 * A NEWLY CREATED accessory does exactly that: `useAccessoryForm` auto-fires
 * `rating_mode:activate` for the fresh row, the dialog mounts, and the seed
 * clones `{}`. The Mounting Plane select then renders its `- -` placeholder with
 * nothing selected (the OPTIONS are fine — they come from the CMS doc), the
 * Available-Airway labels resolve off a null plane, and `init_position` stays
 * `{}`, so `selectAccessory`'s POSITION_FIELDS diff reports every field as
 * changed while `mounting_plane_id` — never staged — is absent from it. Closing
 * and reopening the dialog fixed it, which is the tell: the second open reads a
 * position that has since arrived.
 *
 * Waiting here rather than re-seeding mid-flight makes that known-good second
 * open the ONLY path: every init effect in the body still runs exactly once, in
 * order, with both the shim and the position present — and `stagingPosition`,
 * `initPosition`, the `StagingPositionAdapter` and the MountingModel are all
 * built from one consistent snapshot instead of being patched up afterwards.
 * Same shape as <FilterSelector>'s shim gate, and the same preloader.
 *
 * `positionReady()` goes true as soon as the boot SETTLES — including when it
 * decides no position is coming (`withPosition: false`, or an unplaced
 * `loadPositionWhenAlreadySelected` row) and when the fetch failed — so this
 * cannot become a stuck preloader. Shims without it read as ready.
 */
export const AccessorySelector = (props: AccessorySelectorProps) => {
  const { modelId } = props;
  const ready = useSyncExternalStore(subscribeFormShims, () => {
    const shim = window.__FORM_SHIM__?.[modelId];
    if (!shim) return false;
    return shim.positionReady ? shim.positionReady() : true;
  });
  // ONE-WAY latch (FilterSelector's rationale verbatim): the gate only ever
  // holds the body back BEFORE its first mount. A shim that disappears later —
  // the host form unmounts on an ACCESSORIES → COMPONENTS tab roundtrip while
  // the dialog is open — must NOT tear the mounted body down and drop its state.
  const mounted = useRef(false);
  if (ready) mounted.current = true;
  if (!mounted.current) return <div className="preloader dotted-preloader active" />;
  return <AccessorySelectorBody {...props} />;
};

const AccessorySelectorBody = ({ type, modelId, activate, setSelectEnabled, ahu }: AccessorySelectorProps) => {
  const dispatch = useAppDispatch();
  const accessoryType = type;
  const section = (activate.section ?? {}) as Record<string, unknown> & { id: number };
  const sectionId = Number(section.id);

  // the GLOBAL AHU cms doc (legacy self.globalCMS / fn['current.cms']) —
  // `air.air_stream` feeds the Air Stream select. The per-type cms doc
  // (opening/hoods/louvers `params/`) never carries an `air_stream` key, so
  // reading air streams off it (as this selector used to) left the select
  // with nothing but its blank placeholder — same bug, same fix, as
  // EvapPackSelector/GasHeaterSelector/CoilSelector's `globalDoc`.
  const ahuMfgId = Number(
    (ahu as Record<string, unknown> | null | undefined)?.mfg_id ??
      (ahu as Record<string, unknown> | null | undefined)?.manufacturer_id ??
      1
  );
  const ahuCcId = Number(
    (ahu as Record<string, unknown> | null | undefined)?.construction_class_id ?? 1
  );
  const { data: globalDoc } = useGetCmsAhuDocQuery({ mfgId: ahuMfgId, constructionClassId: ahuCcId });

  // form shim (current_model.view analogue).
  const getShim = useCallback(
    (): ComponentFormShim | undefined => window.__FORM_SHIM__?.[modelId],
    [modelId]
  );

  // ---- staging models (clones — legacy staging_model / staging_position) ----
  const stagingModel = useRef<StagingModel | null>(null);
  const stagingPosition = useRef<StagingModel | null>(null);
  const init = useRef<Record<string, unknown>>({});
  const initPosition = useRef<Record<string, unknown> | null>(null);
  const seeded = useRef(false);
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // userArmed gate — nothing mutates until a real user change.
  const userArmed = useRef(false);

  // ---- CMS (per-type; damper = 10 parallel merged) ----
  const [cms, setCms] = useState<Record<string, unknown> | null>(null);
  const staticDamperCms = useRef<Record<string, unknown> | null>(null);
  const lastDamperCmsUrl = useRef<string | null>(null);
  const catalogBase = useRef<string | null>(null);
  const catalogExtraParams = useRef<string | null>(null);

  // ---- right-panel tab + zoom ----
  const [dataTab, setDataTab] = useState<DataTab>('ahu');
  /**
   * ★ THE TAB IS ALSO A REF, and `loadSmartlist` reads the REF, not the state.
   *
   * Legacy resolves "is the Smartlist open?" by reading the LIVE attribute
   * inside `loadSmartlist`'s debounced body — `self.$el.find(
   * '.mounting_popup_wrapper').attr('data-tab') === 'smartlist'`
   * (accessory.selector.js:3039) — and `showSmartlist` (:3274-3284) sets that
   * attribute SYNCHRONOUSLY on the line before it calls `loadSmartlist()`. So
   * legacy's debounce always sees the post-toggle value.
   *
   * React state cannot be read that way. `setDataTab(next)` does not mutate
   * `dataTab` in the handler's scope, so a handler that calls the setter and
   * then `loadSmartlist()` hands the debounce closure the PRE-toggle value —
   * `isSmartlistOpen` comes out false, the items request is replaced by
   * `Promise.resolve(null)`, and the panel opens on an empty list while the
   * badge (fed by the separate count request) still shows a real number.
   * Measured on dev3 damper 25836: 0 rows / "No results" / badge 62 / 5813086,
   * and ZERO catalog GETs after the toggle.
   *
   * Keeping a ref in lockstep with the state is the faithful equivalent of
   * legacy's DOM attribute: written synchronously by `setTab`, read at
   * debounce-fire time. It also preserves the behaviour legacy gets for free —
   * opening the panel DURING the 300 ms debounce window still yields items,
   * because the read happens when the timer fires, not when it is scheduled.
   */
  const dataTabRef = useRef<DataTab>('ahu');
  const setTab = useCallback((t: DataTab) => {
    dataTabRef.current = t;
    setDataTab(t);
  }, []);
  const [dataZoom, setDataZoom] = useState<'zoom-in' | 'zoom-out'>('zoom-in');

  // ---- smartlist ----
  const [smartlist, setSmartlist] = useState<{
    items: any[];
    count: number | null;
    cmsCount: number | null;
    /** legacy `smartlistIsLoading` (:3032), split by which spinner it draws:
     *  'initial' — `loadSmartlist`'s page-1 fetch; legacy empties the rows and
     *              puts a centre spinner in their place (:3034-3036), so
     *              "No results" is only ever the ANSWER, never the wait.
     *  'more'    — `loadMoreSmartlist`'s append; rows stay, a bottom spinner is
     *              pushed after them (:3148-3150).
     * Both states are the load-more guard, as legacy's single flag is. */
    loading: 'none' | 'initial' | 'more';
  }>({ items: [], count: null, cmsCount: null, loading: 'none' });
  const smartlistReqId = useRef(0);
  const smartlistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const paramsChangedAutoPick = useRef(false);
  /** Infinite-scroll bookkeeping. Legacy keeps these as view instance fields
   * (`smartlistPage` / `smartlistHasMore` / `smartlistIsLoading` /
   * `smartlistData`) and reads them inside async callbacks and the `onscroll`
   * handler, so they are refs here too: the scroll handler must see the value
   * the last response wrote, not the one its closure was built with. */
  const smartlistPage = useRef(1);
  const smartlistHasMore = useRef(true);
  const smartlistLoading = useRef<'none' | 'initial' | 'more'>('none');
  /** legacy `smartlistData` — the authoritative accumulated rows. */
  const smartlistItems = useRef<any[]>([]);
  const smartlistCount = useRef<number | null>(null);
  /** legacy's page size (`newItems.length === 50` is how both loaders decide
   * there is another page); the CMS `…/all/` endpoints paginate by 50. */
  const SMARTLIST_PAGE_SIZE = 50;

  /** The smartlist's column heading (`ul.list-heading`) is a SIBLING of the rows
   * list, not a `<thead>` inside it, so the two do not share a scroll box. Once
   * the damper list grew its FRAME column the columns are wider than the panel
   * — and QA's call (2026-09-08) was to scroll rather than widen the panel — so
   * the heading has to be dragged along by hand or it stays put while the rows
   * slide under it.
   *
   * Exactly the mechanism the Fan VFD mount smartlist already uses
   * (`FanVfdTab.tsx:654-658`, CSS at `common.css:20339-20368`); this is that
   * pattern applied to the accessory mounting smartlist. */
  const smartlistHeadingRef = useRef<HTMLUListElement | null>(null);
  /** WIDTH/HEIGHT column width in the smartlist. Legacy is `w-80` for both
   * damper and louver (`damper.selector.tmpl:691-692`, `louver.selector.tmpl:669-670`);
   * QA asked for `w-70` on 2026-09-08, and only for the damper dialog. */
  const whCol = accessoryType === 'damper' ? 'w-70' : 'w-80';
  /** Forward handle to `loadMoreSmartlist`, declared with the other smartlist
   * code further down; naming it in the dep array here would be a TDZ error
   * (same device as `fetchBladeOrientationsRef`). */
  const loadMoreSmartlistRef = useRef<(() => void) | null>(null);
  const onSmartlistScroll = useCallback((e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    if (smartlistHeadingRef.current) {
      smartlistHeadingRef.current.scrollLeft = el.scrollLeft;
    }
    // ★ legacy wires infinite scroll in `render()` (:431-439): every render
    // re-assigns `.smartlist-items`' `onscroll` to a handler that calls
    // `loadMoreSmartlist()` once the viewport is within 100px of the bottom.
    // The port kept the element's `onScroll` for the heading sync above and
    // dropped the load-more, so the damper list stopped at the first 50 rows
    // however far you scrolled. `loadMoreSmartlist` holds all of legacy's
    // guards (combo mode, in-flight, no-more-pages), so firing it on every
    // scroll event — including the horizontal ones this handler exists for —
    // is what legacy does too.
    if (el.scrollHeight - (el.scrollTop + el.clientHeight) < 100) {
      loadMoreSmartlistRef.current?.();
    }
  }, []);

  // ---- louver blade orientations ----
  const [bladeOrientations, setBladeOrientations] = useState<any[] | null>(null);

  /**
   * ★ `_syncManufacturerSeriesFilters` is BOUNDED in legacy, not run to
   * convergence — see `applyCascadeNow` below. Set by every synchronous call
   * site; consumed (and cleared) by the render-counter effect, which stands in
   * for legacy's `render()` tail (:383) — and legacy does NOT render from
   * `changeParamsField` (:2467) or `_updateBladeOrientationSelect` (:1958).
   */
  const cascadeDone = useRef(false);

  // =========================================================================
  // COMBO LOUVER (KA-5245) — sub-phase A1.1: detection + read-only render.
  // A combo louver is the SAME `louver` dialog with `is_combo = true`; there is
  // no separate model, API or selector (inventory §1).
  // =========================================================================
  /** `self._comboMode` — accessory.selector.js:127. Computed ONCE at seed from
   * the staging clone, exactly like legacy: `grep -n "_comboMode\s*=" ` has ONE
   * hit, so `_comboMode === false && current_model.is_combo === true` is
   * unreachable and NOTHING converts a combo back to a plain louver
   * (inventory DEAD-1 — do not add a toggle). */
  const comboMode = useRef(false);
  /** `self._comboFaces` — the per-face bag (front/left/right). */
  const comboFaces = useRef<ComboFacesState | null>(null);
  /** `self._comboAwDims` — the per-face option lists, filled by A1.2's engine
   * from `/louvers/aw_dims/` (two parallel calls: `&front=true` + `&side=true`;
   * left/right SHARE the `side` list). Empty until the shared catalog identity is
   * fully picked, which is also legacy's state for a fresh combo. */
  const comboAwDims = useRef<ComboAwDims>(emptyComboAwDims());
  /** `self._comboAutoSizeEnabled` :144-146 — DEFAULT ON. */
  const comboAutoSizeEnabled = useRef(true);
  /** `self._enclosureMaterials` :129 — GET .../enclosure_materials/ (§3.1 R3). */
  const [enclosureMaterials, setEnclosureMaterials] = useState<
    Array<{ id: number; name: string }> | null
  >(null);
  /** A1.2/A1.3 — the async engine (aw_dims, per-face lookups, the resize
   * round-trips). Created ONCE, lazily; see getComboEngine. */
  const comboEngineRef = useRef<ComboEngine | null>(null);
  /** callback bridge: the engine is built once but must always reach the LATEST
   * useCallback identities (getAccessoryData / updateCanvasPreview /
   * setImgComponent are all declared below it). */
  const comboCbs = useRef({
    getAccessoryData: async (): Promise<void> => undefined,
    updateCanvasPreview: (): void => undefined,
    setImgComponent: (): void => undefined,
  });
  /** `cms` mirrored into a ref — the engine ctx is captured once, so it cannot
   * close over the state value; `isSelectValid` (declared above the CMS state's
   * consumers) reads the combo arm's smartlist counter off it too. Refreshed
   * every render, just below the JSX gate. */
  const cmsRef = useRef<Record<string, unknown> | null>(null);
  /** A1.5 — is the portalled *How to* popup open? `openComboLouverHowTo`
   * :3374-3391 TOGGLES it (a second click on the menu closes it). */
  const [howToOpen, setHowToOpen] = useState(false);

  // ---- mounting image (component tab) ----
  const [imgState, setImgState] = useState<{ src: string; label: string }>({ src: '', label: '' });

  // ---- mini-3D preview canvas (REUSE) ----
  const preview = useAccessoryPreviewCanvas({
    accessoryType,
    accessoryId: modelId,
    cms: cms ?? {},
    section,
  });
  const ahuHostRef = useRef<HTMLDivElement | null>(null);
  const mountingHostRef = useRef<HTMLDivElement | null>(null);

  // ---- staging mounting adapters (REUSE MountingModel + popup) ----
  const stagingForm = useRef<StagingFormAdapter | null>(null);
  const stagingPosAdapter = useRef<StagingPositionAdapter | null>(null);
  const mountingModel = useRef<any>(null);
  const positionDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // =========================================================================
  // SEED — staging clones + CMS (legacy initialize) — NO write fires here.
  // =========================================================================
  useEffect(() => {
    if (seeded.current) return;
    let cancelled = false;
    const shim = getShim();
    if (!shim) return;
    const modelJSON = shim.model.toJSON();
    // position snapshot — the live form holds the AccessoryPositionModel; the
    // selector clones it (legacy current_model.position.toJSON()). Read it off
    // the shim's getPosition (added 6e2c-3) — the dataGetterForRendering reach
    // is a no-op because the forms don't pass a getter.
    const posJSON = shim.getPosition ? shim.getPosition() : {};

    init.current = { ...modelJSON };
    initPosition.current = posJSON ? { ...posJSON } : null;
    const sm = new StagingModel(modelJSON);
    const sp = new StagingModel(posJSON ?? {});
    // COMBO LOUVER — `_isComboInit` :117 / `_comboMode` :127. `is_combo` is
    // already on the staging clone (the server sets it at create, §4 W1).
    const isComboInit = accessoryType === 'louver' && !!modelJSON.is_combo;
    comboMode.current = isComboInit;
    // `self._comboFaces` :128 — built for EVERY louver (legacy gates on the
    // accessory type, not on combo mode) so a plain louver keeps a null bag.
    comboFaces.current = accessoryType === 'louver' ? initComboFaces(modelJSON) : null;
    comboAutoSizeEnabled.current = comboAutoSizeEnabledFromModel(modelJSON);
    // desired_face_velocity_actual (localStorage side channel #349).
    // COMBO: the fallback is 500, NOT the view's 1200 — accessory.selector.js
    // :115-123 ("A new combo louver defaults to 500 fpm desired face velocity; a
    // plain louver keeps the view default") and again at render :350.
    const fv =
      (modelJSON.desired_face_velocity_actual as number | undefined) ||
      Number(localStorage.getItem(faceVelKey(modelId))) ||
      (isComboInit
        ? COMBO_DEFAULT_FACE_VELOCITY
        : Number(shim.defaultFaceVelocity) || FACE_VEL_DEFAULT[accessoryType]);
    sm.set({ desired_face_velocity_actual: fv });
    // `if (self._comboMode) { self._fetchEnclosureMaterials(); self._applyEnclosureDefault(); }`
    // :147. Both are SILENT staging writes — no network mutation, nothing marked
    // changed (A1.1 is zero-write). `applyEnclosureDefaults` is deliberately
    // handed an EMPTY cms because legacy runs at :147, before
    // `fn['current.cms.louver']` resolves at :201 (see the helper's note).
    if (isComboInit) {
      const defaults = applyEnclosureDefaults(modelJSON, {}, section);
      if (Object.keys(defaults).length) sm.set(defaults);
    }
    stagingModel.current = sm;
    stagingPosition.current = sp;

    // CMS load.
    const loadCms = async () => {
      if (accessoryType === 'damper') {
        const docs = await Promise.all(
          DAMPER_CMS_FIELDS.map((field) =>
            dispatch(
              accessorySelectorApi.endpoints.getAccessoryParamsDoc.initiate(
                `/api/v1.0/cms/1/v1.0.0/ahu/dampers/params/?${field}=true`
              )
            ).unwrap()
          )
        );
        const merged: Record<string, unknown> = {};
        docs.forEach((d) => Object.assign(merged, (d as any)?.damper ?? {}));
        if (cancelled) return;
        staticDamperCms.current = merged;
        setCms(merged);
      } else {
        const raw = await dispatch(
          accessorySelectorApi.endpoints.getAccessoryParamsDoc.initiate(
            CMS_URL_FOR_TYPE[accessoryType]
          )
        ).unwrap();
        if (cancelled) return;
        setCms(unwrapAccessoryCms(raw as Record<string, unknown>, accessoryType));
      }
    };
    // COMBO LOUVER — `_fetchEnclosureMaterials` :1874-1908. Fired at :147, i.e.
    // BEFORE the CMS load, and on resolve legacy calls `render()` (:1903) — here
    // the setState does that. Read-only GET.
    const loadEnclosureMaterials = async () => {
      try {
        const data = await dispatch(
          accessorySelectorApi.endpoints.getEnclosureMaterials.initiate(modelId)
        ).unwrap();
        if (cancelled) return;
        setEnclosureMaterials(data.materials);
        // :1893-1902 — default the material to the AHU's casing outer skin when
        // none is set, and cache the name alongside the id in louver_params
        // (KA-5250). A no-op in practice: the server already stamps
        // `enclosure_material_id` (697) in save(). Silent staging write.
        if (data.default != null && !sm.get('enclosure_material_id')) {
          const defaultMaterial = data.materials.find((m) => m.id === data.default);
          const materialParams = parseParams(sm.get('louver_params'));
          materialParams.material_id = data.default;
          materialParams.material_name = defaultMaterial ? defaultMaterial.name : null;
          sm.set({
            enclosure_material_id: data.default,
            louver_params: JSON.stringify(materialParams),
          });
        }
      } catch {
        // :1904-1907 — swallow and fall back to an empty list.
        if (!cancelled) setEnclosureMaterials([]);
      }
    };
    void loadCms();
    if (isComboInit) void loadEnclosureMaterials();
    seeded.current = true;
    bump();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessoryType, dispatch, getShim, modelId, bump]);

  // checkButtonsAvailability: accessory selectors start with #select gated by
  // _isSelectValid (legacy _updateSelectButtonState). This does NOT arm
  // userArmed and does NOT mark the chassis dirty.
  const isSelectValid = useCallback((): boolean => {
    const m = stagingModel.current;
    if (!m) return false;
    if (accessoryType === 'opening') return m.get('width') != null && m.get('height') != null;
    if (accessoryType === 'damper') return m.get('master_damper_id') != null;
    // ---- COMBO LOUVER — A1.4: `_isSelectValid`'s combo arm (:3577-3591) ------
    // A combo has NO single `master_louver_id` (§1 — the faces carry the catalog
    // identity), so the plain-louver test below can never pass for one. It is
    // selectable when the SHARED identity is picked AND the checked faces are
    // sized AND at least one `combo_smartlist` row still matches AND no face is
    // flagged `.not-valid`.
    // ⚠⚠ Q8 IS MIRRORED, NOT FIXED (see `comboFacesSized`): with auto-size ON only
    // `.some()` checked face needs to be sized, so `#select` goes clickable while
    // both side selects render BLANK (the Q2 state). Measured on legacy; a port
    // that required `.every()` on both branches would silently fix it.
    if (accessoryType === 'louver' && comboMode.current) {
      return comboIsSelectValid({
        cms: cmsRef.current,
        params: parseParams(m.get('louver_params')),
        faces: comboFaces.current,
        autoSizeEnabled: comboAutoSizeEnabled.current,
      });
    }
    if (accessoryType === 'louver')
      return m.get('master_louver_id') != null && m.get('blade_direction_id') != null;
    if (accessoryType === 'hood') {
      const width = m.get('width');
      const height = m.get('height');
      if (width == null || height == null) return false;
      const styleId = m.get('style_id');
      if (styleId != null) {
        const depth = m.get('depth');
        const height2 = m.get('height_2');
        if ((depth == null && m.get('depth_mode_id') != 1) || (height2 == null && styleId == 4))
          return false;
      }
      return true;
    }
    return true;
  }, [accessoryType]);

  // sync the chassis #select gate whenever staging changes.
  useEffect(() => {
    // COMBO LOUVER — A1.4: legacy's `render()` TAIL, :441-442 —
    // `self._updateSelectButtonState(); if (self._comboMode)
    // self._applyComboValidation();` — so the `.not-valid` pass is re-applied after
    // EVERY render (which is also how legacy survives `render()` rebuilding the
    // inputs and dropping the classes). This effect has no dependency array, i.e.
    // it IS "after every render". `applyValidation` neither renders nor sets state,
    // so there is no loop; it ends in legacy by re-calling
    // `_updateSelectButtonState()`, which is the `setSelectEnabled` below.
    if (comboMode.current) getComboEngine().applyValidation();
    setSelectEnabled(isSelectValid());
  });

  // mark a real user change (arm persist + chassis dirty tick).
  const markChanged = useCallback(() => {
    userArmed.current = true;
    ahuBoardBus.emit('rating_mode:mounting:changed');
  }, []);

  // =========================================================================
  // COMBO LOUVER (KA-5245) — A1.2 + A1.3: the async engine.
  // Built lazily and ONCE (legacy's methods live on the single View instance),
  // with every live value reached through a getter so nothing goes stale.
  // =========================================================================
  const getComboEngine = useCallback((): ComboEngine => {
    if (!comboEngineRef.current) {
      comboEngineRef.current = createComboEngine({
        // `self.mfg.id` — 1, matching CMS_URL_FOR_TYPE + buildSmartlistUrls.
        cmsMfgId: 1,
        sectionId,
        comboMode: () => comboMode.current,
        faces: () => comboFaces.current,
        awDims: () => comboAwDims.current,
        setAwDims: (dims) => {
          comboAwDims.current = dims;
        },
        autoSizeEnabled: () => comboAutoSizeEnabled.current,
        staging: () => stagingModel.current,
        stagingPosition: () => stagingPosition.current,
        cms: () => cmsRef.current,
        // aw_dims is never memoized (legacy has no stateStorage entry for it and
        // the same URL is legitimately re-requested — Q10), so force a refetch.
        getAwDims: (url) =>
          dispatch(
            comboLouverApi.endpoints.getComboAwDims.initiate(url, { forceRefetch: true })
          ).unwrap(),
        // the per-face lookup IS memoized by URL (legacy stateStorage.models
        // 'combo_master_louver', :1749-1752) → plain RTKQ cache + in-flight dedup.
        getMasterLouvers: (url) =>
          dispatch(comboLouverApi.endpoints.getComboMasterLouvers.initiate(url)).unwrap(),
        patchWithPosition: async (body) => {
          // ⚠ DATA-SAFETY (§4 / 6e2a): /with_position writes dev3, so like
          // getAccessoryData it never fires un-armed. Every engine entry point is
          // reached from a real user action that has already called markChanged().
          if (!userArmed.current) return null;
          try {
            return await dispatch(
              accessorySelectorApi.endpoints.patchAccessoryWithPosition.initiate({
                plural: PLURAL.louver,
                id: modelId,
                body,
              })
            ).unwrap();
          } catch {
            // legacy: fn.uni_tools.patch retries once then throws into a bare
            // .then() — nothing is applied. Same outcome.
            return null;
          }
        },
        render: bump,
        updateCanvasPreview: () => comboCbs.current.updateCanvasPreview(),
        getAccessoryData: () => comboCbs.current.getAccessoryData(),
        setImgComponent: () => comboCbs.current.setImgComponent(),
        markChanged,
        setParamsChangedAutoPick: () => {
          paramsChangedAutoPick.current = true;
        },
        // closeComboSmartlist :1560-1570 — back to the 3-D preview tab (NOT
        // 'mounting', which is where the plain-louver smartlist toggle returns).
        closeSmartlist: () => setTab('ahu'),
        domValue: dialogInputValue,
        // A1.6 — THE persist. `/louvers/<id>` with NO trailing slash (urls.py
        // `r'^louvers/(?P<pk>[0-9]+)$'` — `/louvers/5/` 404s), which is exactly
        // what `patchConcreteRow` builds. ⚠ Unlike `/with_position` this is NOT
        // userArmed-gated: it only ever runs from the Select button, i.e. the most
        // explicit user gesture there is.
        patchLouver: async (body) => {
          try {
            return await dispatch(
              ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
                plural: PLURAL.louver,
                id: modelId,
                data: body,
              })
            ).unwrap();
          } catch {
            // legacy: `fn.uni_tools.patch(..., {retries:1})` throws into a bare
            // `if (data)`, so nothing is applied. Same outcome.
            return null;
          }
        },
        sectionAwHeight: () => section.aw_height,
      });
    }
    return comboEngineRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, modelId, sectionId, bump, markChanged]);

  // =========================================================================
  // build the with_position body (getAccessoryData payload — CODE-ONLY write)
  // =========================================================================
  const buildWithPositionBody = useCallback(
    (mountingPlaneIdChanged: boolean): WithPositionBody | null => {
      const m = stagingModel.current?.toJSON();
      const position = stagingPosition.current?.toJSON();
      if (!m || !position) return null;
      const paramsKey = `${accessoryType}_params`;
      const params = m[paramsKey];
      const accessoryFields: Record<string, unknown> = { tag: m.tag || '' };
      CHANGEABLE_FIELDS[accessoryType].forEach((f) => {
        accessoryFields[f] = m[f];
      });
      if (params != null) accessoryFields[paramsKey] = params;
      const masterIdField = `master_${accessoryType}_id`;
      if (accessoryFields[masterIdField] == null) delete accessoryFields[masterIdField];
      if (!mountingPlaneIdChanged) accessoryFields.is_blade_direction_changed = true;
      if (!accessoryFields.blade_direction_id) delete accessoryFields.blade_direction_id;
      if (!accessoryFields.air_direction_id) delete accessoryFields.air_direction_id;
      if (accessoryFields.depth_mode_id == 1 && !accessoryFields.depth)
        accessoryFields.depth = accessoryFields.height;
      if (accessoryType === 'damper' && !accessoryFields.hood_id && !accessoryFields.louver_id)
        accessoryFields.position_id = modelId;
      if (accessoryType === 'hood') {
        const prevStyle = init.current.style_id ?? null;
        accessoryFields.style_id_changed = m.style_id != prevStyle;
        if (accessoryFields.style_id_changed) {
          delete accessoryFields.angle_1;
          delete accessoryFields.angle_2;
          delete accessoryFields.angle_3;
          delete accessoryFields.angle_4;
        }
      }
      const positionFields: Record<string, unknown> = {
        x: position.x,
        y: position.y,
        mounting_plane_id:
          accessoryType === 'damper' && (accessoryFields.hood_id || accessoryFields.louver_id)
            ? undefined
            : position.mounting_plane_id,
        default_pos_x: position.default_pos_x,
        default_pos_y: position.default_pos_y,
        mounting_type: position.mounting_type,
      };
      if (positionFields.x === null) delete positionFields.x;
      if (positionFields.y === null) delete positionFields.y;
      const body: WithPositionBody = { [accessoryType]: accessoryFields, position: positionFields };
      if (body.position.default_pos_x || (body.position.x as number) < 0) delete body.position.x;
      if (body.position.default_pos_y || (body.position.y as number) < 0) delete body.position.y;
      return body;
    },
    [accessoryType, modelId]
  );

  const mountingPlaneIdChangedRef = useRef(false);
  const checkSelectsRef = useRef(false);

  // getAccessoryData — THE staging round-trip (⚠ writes dev3; userArmed-gated).
  const getAccessoryData = useCallback(async () => {
    if (!userArmed.current) return; // data-safety: never fire un-armed.
    const body = buildWithPositionBody(mountingPlaneIdChangedRef.current);
    if (!body) return;
    const paramsKey = `${accessoryType}_params`;
    // legacy snapshots the model at :3620, i.e. BEFORE the await below — which is
    // precisely why the latestEnclosure guard downstream exists.
    const modelSnapshot = stagingModel.current?.toJSON() ?? {};
    // ---- COMBO LOUVER — A1.3: the combo sizing merge (:3656-3693) ----------
    // Carry the same payload _comboAutoSize/_comboRecalc send, so THIS round trip
    // (the one that actually lands mounting_plane_id on the staging model) also
    // resizes atomically instead of racing a separate debounced auto-size.
    if (comboMode.current) {
      const engine = getComboEngine();
      // ★ RACE GUARD 1 (:3667) — a field change that just kicked off a per-face
      // master_louver_id lookup may be immediately followed by another field's
      // round trip. Wait for it, or this body snapshots the pre-lookup value
      // (usually null) and the just-picked louver drops out of the request.
      await engine.awaitPendingLookups();
      // these two are read off the DOM *after* the await, exactly as legacy does.
      const depthVal = dialogInputValue('enclosure_depth');
      const fvVal = dialogInputValue('desired_face_velocity_actual');
      Object.assign(body[accessoryType], {
        section_id: sectionId,
        is_combo: true,
        auto_size: comboAutoSizeEnabled.current,
        enclosure_depth: depthVal ? comboNum(depthVal) : comboNum(modelSnapshot.enclosure_depth),
        // ⚠ Q13: INCLUDED here but deliberately OMITTED by _comboAutoSize, whose
        // body lets the server re-derive them from the fitted front airway.
        enclosure_width: comboNum(modelSnapshot.enclosure_width),
        enclosure_height: comboNum(modelSnapshot.enclosure_height),
        // Auto-size OFF and already sized → omit (send null) so the server runs
        // combo_fit_enclosure_to_faces (grow-only) instead of the sides-first
        // resize. ON, or never sized (no other way to establish a first size) →
        // include it and take server mode 1.
        desired_face_velocity: engine.shouldAutoSize() ? comboNum(fvVal) : null,
        combo_faces: engine.faceBodies(true),
      });
    }
    try {
      const data = await dispatch(
        accessorySelectorApi.endpoints.patchAccessoryWithPosition.initiate({
          plural: PLURAL[accessoryType],
          id: modelId,
          body,
        })
      ).unwrap();
      mountingPlaneIdChangedRef.current = false;
      // ★ RACE GUARD 2a (:3735) — re-read the params HERE, not from a snapshot
      // taken before the request: a concurrent update (e.g. blade-orientation
      // auto-select firing while this PATCH was in flight) may have changed them
      // during the round trip, and reapplying the stale snapshot would silently
      // revert it once the re-render lands.
      const latestParams = stagingModel.current?.get(paramsKey);
      // ★ RACE GUARD 2b (:3746-3750) — the same race for the combo enclosure
      // size fields, whose request body came off the PRE-await modelSnapshot. If
      // the user edits W/H/D while the request is in flight the server echoes the
      // pre-edit value it was sent, and applying it would undo their newer edit.
      // ONLY guarded while auto-size is OFF — with it on those inputs are
      // disabled (no direct edit possible) and the server's GROWN values are the
      // ones that must land.
      // ★ RACE GUARD 2c — the CATALOGUE SELECTION. `buildWithPositionBody` drops
      // `master_<type>_id` from the body when it is null (the smartlist clears it
      // the moment the filters stop resolving to exactly one row), so the preview
      // answers with the value the DATABASE still holds and re-installs a model
      // the user has just filtered away: #select re-enables with no row marked,
      // and pressing it saves the old catalogue model under the new
      // manufacturer's parameters. Read AFTER the await for the same reason as
      // 2a — the smartlist's own debounce may have landed a NEWER pick while this
      // was in flight — and reapplied unconditionally, `null` included, because a
      // cleared selection is exactly what has to survive the reply.
      // Damper and louver only: they are the two accessories with a catalogue.
      const guardsMaster = accessoryType === 'damper' || accessoryType === 'louver';
      const masterKey = `master_${accessoryType}_id`;
      const latestMaster = guardsMaster
        ? (stagingModel.current?.get(masterKey) ?? null)
        : undefined;
      const latestEnclosure =
        comboMode.current && !comboAutoSizeEnabled.current
          ? {
              enclosure_width: stagingModel.current?.get('enclosure_width'),
              enclosure_height: stagingModel.current?.get('enclosure_height'),
              enclosure_depth: stagingModel.current?.get('enclosure_depth'),
            }
          : null;
      if (data?.position) stagingPosition.current?.set(data.position);
      if (data?.[accessoryType]) stagingModel.current?.set(data[accessoryType] as Record<string, unknown>);
      if (latestParams) stagingModel.current?.set({ [paramsKey]: latestParams });
      if (guardsMaster) stagingModel.current?.set({ [masterKey]: latestMaster });
      if (latestEnclosure) stagingModel.current?.set(latestEnclosure);
      // COMBO — reflect the (possibly just-resized) per-face airways (:3762-3772).
      // Gated on shouldAutoSize() so that with auto-size off (and the combo
      // already sized) changing an UNRELATED field through this round trip — air
      // direction, casing mount, mounting plane — never silently rewrites the face
      // W×H underneath the user; a never-sized combo still needs it for its first
      // faces.
      if (comboMode.current && getComboEngine().shouldAutoSize()) {
        const rows = (data?.[accessoryType] as Record<string, unknown> | undefined)?.combo_faces;
        if (Array.isArray(rows)) {
          getComboEngine().applyServerFaces(rows as Array<Record<string, unknown>>);
        }
      }
      updateCanvasPreview();
      await updateDamperCms();
      bump();
      // COMBO — re-fire auto-size on completion (:3785-3794). The 350 ms debounce
      // in changeModelField/changeParamsField usually fires BEFORE this async
      // round trip returns, so _comboReady() was still false (no
      // mounting_plane_id on staging yet) and it gave up with nothing to re-fire
      // it. Firing here makes auto-size independent of which field changed.
      if (comboMode.current && getComboEngine().shouldAutoSize()) {
        void getComboEngine().maybeAutoSize();
      }
    } catch {
      /* legacy retries:1; on failure leave staging unchanged */
    }
  }, [accessoryType, buildWithPositionBody, dispatch, modelId, sectionId, getComboEngine, bump]); // eslint-disable-line

  // =========================================================================
  // damper dynamic CMS swap (_updateDamperCMS — fan-intake / hood / static).
  // =========================================================================
  const updateDamperCms = useCallback(async () => {
    if (accessoryType !== 'damper') return;
    const m = stagingModel.current?.toJSON();
    if (!m) return;
    const planeValue = mountingPlaneValue(cms ?? {}, stagingPosition.current?.get('mounting_plane_id'));
    const { cmsUrl, catalogBase: cb, catalogExtraParams: cep } = resolveDamperCmsUrl({
      mfgId: 1,
      hoodId: m.hood_id,
      mountingPlaneValue: planeValue,
      model: m,
    });
    catalogBase.current = cb;
    catalogExtraParams.current = cep;
    if (cmsUrl === 'static') {
      if (lastDamperCmsUrl.current !== 'static') {
        lastDamperCmsUrl.current = 'static';
        if (staticDamperCms.current) setCms(staticDamperCms.current);
      }
      return;
    }
    if (!cmsUrl || cmsUrl === lastDamperCmsUrl.current) return;
    lastDamperCmsUrl.current = cmsUrl;
    try {
      const data = await dispatch(
        accessorySelectorApi.endpoints.getAccessoryParamsDoc.initiate(cmsUrl)
      ).unwrap();
      const cmsData = (data as any)?.damper;
      if (cmsData && cmsUrl === lastDamperCmsUrl.current) {
        setCms({ ...(staticDamperCms.current ?? {}), ...cmsData });
        loadSmartlist();
      }
    } catch {
      /* keep static cms */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessoryType, cms, dispatch]);

  // =========================================================================
  // canvas preview update — delegates to the reused mini-3D hook.
  // =========================================================================
  const updateCanvasPreview = useCallback(() => {
    const m = stagingModel.current?.toJSON();
    const p = stagingPosition.current?.toJSON();
    if (!m || !p) return;
    // COMBO LOUVER — A2.1 (`_updateCanvasPreview` :3415-3428 / :3446). The
    // selector owns `_comboMode` / `_comboFaces` / `_comboLouverCounts`, so the
    // combo working state is assembled here and folded into the payload by the
    // hook. Legacy's gate is `self._comboMode && self._comboFaces`.
    if (comboMode.current && comboFaces.current) {
      preview.updatePreview(m, p, {
        comboFaces: buildComboCanvasFaces(comboFaces.current),
        ambiguousMatch:
          comboLouverCounts(cms, parseParams(m[`${accessoryType}_params`])).count > 1,
      });
      return;
    }
    preview.updatePreview(m, p);
  }, [preview, accessoryType, cms]);

  // =========================================================================
  // SMARTLIST (damper + louver only).
  // =========================================================================
  /** Forward handle to `fetchBladeOrientations`, which is declared below. */
  const fetchBladeOrientationsRef = useRef<((autoSelect: boolean) => void) | null>(null);

  const applySmartlistItem = useCallback(
    async (id: number, items: any[]) => {
      const m = stagingModel.current;
      if (!m) return;
      const targetField = `master_${accessoryType}_id`;
      const paramsKey = `${accessoryType}_params`;
      const item = items.find((i) => i.id === id);
      const updates: Record<string, unknown> = { [targetField]: id };
      if (item) {
        const params = parseParams(m.get(paramsKey));
        Object.keys(item).forEach((k) => {
          if (k !== 'id' && !k.endsWith('_name') && item[k] != null) params[k] = item[k];
        });
        updates[paramsKey] = JSON.stringify(params);
      }
      m.set(updates);
      markChanged();
      await getAccessoryData();
      bump();
      // ★ legacy's LAST line — `_applySmartlistItem` :3306-3307 is
      // `await self.getAccessoryData(); self._fetchLouverBladeOrientations(true);`
      // and the port stopped at the `await`. The re-fetch is what narrows the
      // orientation list to the manufacturer+series the pick just installed; when
      // exactly one remains, the sole-option auto-pick (:1949-1955) writes BOTH
      // `blade_orientation_id` and `blade_orientation_name` into `<type>_params`.
      //
      // Harmless while Select discarded everything — but once Select persists,
      // the omission becomes a stored-data divergence. Measured on dev3, plain
      // louver, same catalog pick 204007535 on both stacks:
      //   legacy 25844  …,"frame_mtl_id":4503,"blade_orientation_id":1,
      //                 "blade_orientation_name":"Horizontal"
      //   port   25839  …,"frame_mtl_id":4503            ← both keys missing
      // `fetchBladeOrientations` no-ops for damper, so this is louver-only.
      //
      // Called through a ref because it is declared further down the component;
      // naming it in this callback's dep array would be a TDZ error.
      fetchBladeOrientationsRef.current?.(true);
    },
    [accessoryType, markChanged, getAccessoryData, bump]
  );

  const loadSmartlist = useCallback(() => {
    if (accessoryType !== 'damper' && accessoryType !== 'louver') return;
    // COMBO LOUVER — QUIRK Q12 (inventory §8): combo render() NEVER runs
    // loadSmartlist / updateSmartlistDOM / appendSmartlistItems /
    // loadMoreSmartlist; all four early-return on `_comboMode` (:3011, :3123,
    // :3165, :3206) because the combo smartlist is TEMPLATE-rendered out of
    // `cms.combo_smartlist` (43 product lines, no sizes) and the generic per-part
    // loader would wipe it. Consequence to preserve: there is NO infinite scroll
    // on the combo smartlist. A1.2 renders those static rows.
    if (comboMode.current) return;
    if (smartlistTimer.current) clearTimeout(smartlistTimer.current);
    smartlistTimer.current = setTimeout(() => {
      const m = stagingModel.current;
      if (!m) return;
      const paramsKey = `${accessoryType}_params`;
      const params = parseParams(m.get(paramsKey));
      const { itemsUrl, countUrl, totalUrl } = buildSmartlistUrls({
        type: accessoryType,
        mfgId: 1,
        params,
        page: 1,
        catalogBase: catalogBase.current,
        catalogExtraParams: catalogExtraParams.current,
      });
      const reqId = ++smartlistReqId.current;
      // ★ legacy :3030-3036, and the half of the reported bug that is visible
      // before anything comes back: the page counter is rewound and the rows
      // are REPLACED by the centre spinner, synchronously, before the fetch
      // goes out. Without it the list keeps rendering `items.length === 0` as
      // "No results" for the whole round-trip, so every open and every params
      // edit flashes a wrong answer where legacy shows a spinner.
      smartlistPage.current = 1;
      smartlistHasMore.current = true;
      smartlistLoading.current = 'initial';
      setSmartlist((s) => ({ ...s, loading: 'initial' }));
      // ★ the REF, read at debounce-fire time — legacy's live `data-tab` read
      // (:3039). Reading the state variable here captures whatever the tab was
      // when this closure was built, i.e. the PRE-toggle value on the one call
      // site that matters (see the `dataTabRef` doc block above).
      const isSmartlistOpen = dataTabRef.current === 'smartlist';
      void Promise.all([
        isSmartlistOpen
          ? dispatch(accessorySelectorApi.endpoints.getSmartlistPage.initiate(itemsUrl)).unwrap()
          : Promise.resolve(null),
        dispatch(accessorySelectorApi.endpoints.getSmartlistCount.initiate(countUrl)).unwrap(),
        dispatch(accessorySelectorApi.endpoints.getSmartlistCount.initiate(totalUrl)).unwrap(),
      ]).then(([items, count, total]) => {
        if (reqId !== smartlistReqId.current) return;
        // legacy :3050-3054 — `smartlistData` / `smartlistHasMore` are only
        // touched when the panel is OPEN (closed, no items request was made);
        // when it is closed the previous rows and count stand.
        const newItems = Array.isArray(items) ? items : smartlistItems.current;
        if (isSmartlistOpen) smartlistHasMore.current = newItems.length === SMARTLIST_PAGE_SIZE;
        const c =
          count != null ? count : isSmartlistOpen ? newItems.length : smartlistCount.current;
        smartlistItems.current = newItems;
        smartlistCount.current = c;
        smartlistLoading.current = 'none';
        setSmartlist({ items: newItems, count: c, cmsCount: total, loading: 'none' });
        // master null when count 0 or >1 (:1376-1382).
        if (c === 0 || (c != null && c > 1)) {
          const masterField = `master_${accessoryType}_id`;
          if (m.get(masterField) != null) {
            m.set({ [masterField]: null });
            bump();
          }
        }
        // auto-pick count===1 (open OR params-changed) (:1399-1420).
        const currentMasterId = m.get(`master_${accessoryType}_id`);
        const openAutoPick = !currentMasterId && c === 1;
        const paramsAutoPick = paramsChangedAutoPick.current;
        if (c === 1 && (openAutoPick || paramsAutoPick)) {
          paramsChangedAutoPick.current = false;
          void dispatch(accessorySelectorApi.endpoints.getSmartlistPage.initiate(itemsUrl))
            .unwrap()
            .then((data) => {
              if (Array.isArray(data) && data.length === 1 && data[0].id !== currentMasterId) {
                void applySmartlistItem(data[0].id, data);
              }
            });
        } else {
          paramsChangedAutoPick.current = false;
        }
      }).catch(() => {
        // Legacy has no rejection path here — it only ever had a DOM spinner
        // and a failed `fn.uni_tools.get` left it spinning. Now that the
        // spinner is state, a stuck 'initial' would hide the list for the rest
        // of the dialog's life, so a failed page falls back to the rows we
        // have (i.e. "No results" on a first load) rather than spinning.
        if (reqId !== smartlistReqId.current) return;
        smartlistLoading.current = 'none';
        setSmartlist((s) => ({ ...s, loading: 'none' }));
      });
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `dataTab` is deliberately NOT a dependency — the open/closed decision is
    // read off `dataTabRef` at fire time, so rebuilding this callback on every
    // tab change would only churn the identity of a debounced function.
  }, [accessoryType, dispatch, applySmartlistItem, bump]);

  /**
   * INFINITE SCROLL — `loadMoreSmartlist` (accessory.selector.js:3117-3160),
   * missing from the port entirely: the damper/louver Smartlist fetched page 1
   * and nothing else, so a 62-match filter showed 50 rows and a 5,813,086-row
   * catalog showed 50 rows, with no way to reach the rest.
   *
   * Legacy's guards, all preserved:
   *  · combo mode never paginates (Q12 — the combo list is template-rendered
   *    from `cms.combo_smartlist`; appending per-size catalog rows to it is
   *    what once put aw_width/aw_height into the combo columns, :3119-3122);
   *  · one request at a time, and none once a page came back short of 50
   *    (`smartlistIsLoading || !smartlistHasMore`, :3124);
   *  · the page counter only advances on a page that actually had rows, so a
   *    trailing empty page ends the scroll instead of looping (:3153-3157).
   *
   * Two deliberate departures from the legacy text:
   *  · legacy's load-more rebuilds the query string INLINE and — unlike
   *    `loadSmartlist` — forgets `delete params.spn`, so page 2+ could be
   *    filtered differently from page 1. Going through `buildSmartlistUrls`
   *    (the same call `loadSmartlist` makes, `page` apart) is what keeps the
   *    pages members of one list;
   *  · the response is dropped if a `loadSmartlist` has started meanwhile
   *    (`smartlistReqId`). Legacy has that race and appends the old filter's
   *    page 2 under the new filter's page 1.
   */
  const loadMoreSmartlist = useCallback(() => {
    if (accessoryType !== 'damper' && accessoryType !== 'louver') return;
    if (comboMode.current) return;
    if (smartlistLoading.current !== 'none' || !smartlistHasMore.current) return;
    const m = stagingModel.current;
    if (!m) return;
    const params = parseParams(m.get(`${accessoryType}_params`));
    const nextPage = smartlistPage.current + 1;
    const { itemsUrl } = buildSmartlistUrls({
      type: accessoryType,
      mfgId: 1,
      params,
      page: nextPage,
      catalogBase: catalogBase.current,
      catalogExtraParams: catalogExtraParams.current,
    });
    const reqId = smartlistReqId.current;
    smartlistLoading.current = 'more';
    setSmartlist((s) => ({ ...s, loading: 'more' }));
    const settle = (next?: (s: typeof smartlist) => typeof smartlist) => {
      smartlistLoading.current = 'none';
      setSmartlist((s) => ({ ...(next ? next(s) : s), loading: 'none' }));
    };
    void dispatch(accessorySelectorApi.endpoints.getSmartlistPage.initiate(itemsUrl))
      .unwrap()
      .then((items) => {
        if (reqId !== smartlistReqId.current) {
          smartlistLoading.current = 'none';
          return;
        }
        const newItems = Array.isArray(items) ? items : [];
        if (!newItems.length) {
          smartlistHasMore.current = false;
          settle();
          return;
        }
        smartlistPage.current = nextPage;
        smartlistHasMore.current = newItems.length === SMARTLIST_PAGE_SIZE;
        const merged = smartlistItems.current.concat(newItems);
        smartlistItems.current = merged;
        settle((s) => ({ ...s, items: merged }));
      })
      .catch(() => {
        // A failed page must not wedge the guard, or the list never grows
        // again; `hasMore` is left alone so the next scroll can retry.
        if (reqId !== smartlistReqId.current) {
          smartlistLoading.current = 'none';
          return;
        }
        settle();
      });
  }, [accessoryType, dispatch]);
  loadMoreSmartlistRef.current = loadMoreSmartlist;

  // initial smartlist + blade orientations once seeded + cms present.
  const initialLoaded = useRef(false);
  const tagFocusTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!seeded.current || !cms || initialLoaded.current) return;
    initialLoaded.current = true;
    // ★ ORDER IS LOAD-BEARING. Legacy runs `_syncManufacturerSeriesFilters()`
    // at the TAIL of `render()` (:383), and `render()` (:260) precedes both
    // `loadSmartlist()` (:271) and `_fetchLouverBladeOrientations(true)` (:272)
    // — so the sole-option auto-picks that pass writes into `<type>_params` are
    // already there when those two build their query strings. Here the cascade
    // is a POST-COMMIT effect (below, keyed on `version`/`cms`) which is
    // declared after this one and therefore runs after it, so on open both
    // fetches used to go out against the UN-cascaded params.
    //
    // That inverted one dialog-open default outright (the reported bug):
    // dev3's louver CMS exposes exactly ONE manufacturer (234 Nailor
    // Industries), so the cascade auto-picks it — and `?mfg_id=234` narrows
    // `louver_blade_orientations` to the single `[Horizontal]` that
    // `fetchBladeOrientations`' own sole-option auto-pick needs. Without the
    // manufacturer the URL is unfiltered, comes back `[Horizontal, Vertical]`,
    // and Blade Orientation is left on `- -` where legacy shows Horizontal.
    // Running the pass here restores legacy's order for the smartlist query
    // too (legacy's `loadSmartlist` likewise sees the auto-picked identity).
    if (applyCascadeNow()) bump();
    // COMBO LOUVER — accessory.selector.js:266-273, the two combo calls that
    // belong to the open sequence and were both missing from the port.
    //
    // :267-269 — a freshly-added combo that has never been sized runs the
    // sides-first auto-size ONCE. Gated on `neverSized()`, so re-opening an
    // already-sized combo cannot overwrite the user's enclosure.
    //
    // :273 — ★ the reported bug. The per-face W × H `<select>`s are built out of
    // `_comboAwDims`, which is EMPTY until this fetch lands, and legacy's
    // template (louver.selector.tmpl:155-168) — like `ComboFaceView` here —
    // renders only options that are in those lists. Without the open-time fetch
    // every face select stayed on `- -` no matter what `combo_faces[]` carried,
    // so a saved combo re-opened with all three Airway rows blank. Nothing else
    // fetches aw_dims on open: the other call sites hang off a field change, a
    // smartlist pick, the auto-size toggle or the Reset span, and the cascade
    // effect's call is gated on the cascade actually amending the params, which
    // it does not do for a combo whose identity is already complete.
    if (comboMode.current && getComboEngine().neverSized()) {
      void getComboEngine().maybeAutoSize();
    }
    loadSmartlist();
    if (accessoryType === 'louver') fetchBladeOrientations(true);
    if (comboMode.current) void getComboEngine().fetchAwDims();
    void updateDamperCms();
    updateCanvasPreview();
    setImgComponent();
    // accessory.selector.js:274-276 — the tail of the same post-cms block:
    // `setTimeout(function(){ self.$el.find('input[name="tag"]').focus().select(); }, 500)`.
    // Opening any accessory dialog puts the caret in Tag with its text selected,
    // so typing replaces the tag outright. The 500 ms is load-bearing ordering,
    // not padding: the left-panel drive effect below fires its own 200 ms burst
    // of `.click()`s on `#show_accessories` and the mounting/performance subtab
    // labels (legacy initialize :67-103), and those move focus. Firing at 0 ms
    // would hand focus straight back to the board. `self.$el` is this dialog's
    // content div, `#rating_mode_<type>_content` (ComponentSelectorDialog:629).
    tagFocusTimer.current = window.setTimeout(() => {
      const tag = document.querySelector<HTMLInputElement>(
        `#rating_mode_${accessoryType}_content input[name="tag"]`
      );
      tag?.focus();
      tag?.select();
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cms]);

  /**
   * ★ `_syncManufacturerSeriesFilters()` run SYNCHRONOUSLY from a handler, the
   * way legacy calls it — from `changeParamsField` (:2467) and from
   * `_updateBladeOrientationSelect` (:1958, only `if (changed)`).
   *
   * ⚠ THE CASCADE IS BOUNDED, NOT RUN TO CONVERGENCE. Legacy's `staging_model`
   * has NO change listener (`grep -n "listenTo(self.staging_model"` → zero
   * hits), so `staging_model.set()` never re-renders and neither of those two
   * call sites is followed by another cascade pass. `loadSmartlist` only calls
   * `updateSmartlistDOM()` (:3068), not `render()`, so it adds no pass either.
   * The port's render-counter effect below IS legacy's `render()` tail (:383) —
   * left unguarded it turns every handler into a two-pass cascade, and pass 2
   * observes a TRANSIENT state that legacy never cascades on.
   *
   * Measured on dev3, plain louver 13899, real `mfg_id = 47` pick:
   *   legacy   pass 1 (`changeParamsField`) clears `series_id 97` (invalid under
   *            mfg 47 + orientation 2) and stops. The in-flight
   *            `?mfg_id=47&series_id=97` fetch then returns `[]`, orientation 2
   *            is invalidated and cleared, and THAT triggers pass 2 — now with
   *            no orientation, so `allowedSeries` is 7, no sole-option auto-pick
   *            fires, and `series_id` stays EMPTY (34 of 42 options hidden).
   *   port     pass 2 ran immediately with orientation 2 still in params, where
   *            `allowedSeries` is the single `1139 / EVH-501D` → the sole-option
   *            rule auto-picked it (40 hidden) and it reached the wire.
   * So the extra pass is not merely redundant: it is the regression. `cascadeDone`
   * makes the effect skip exactly the pass this call site already performed —
   * legacy's bound, not an iteration cap.
   */
  const applyCascadeNow = useCallback((): boolean => {
    const m = stagingModel.current;
    // Same guard set as the effect below, so `cascadeDone` is only ever raised
    // for a commit the effect will actually reach and clear.
    if (!m || !cms || !seeded.current) return false;
    if (accessoryType !== 'damper' && accessoryType !== 'louver') return false;
    const paramsKey = `${accessoryType}_params`;
    const { nextParams } = syncManufacturerSeriesFilters(
      cms,
      accessoryType,
      parseParams(m.get(paramsKey)),
      comboMode.current
    );
    cascadeDone.current = true;
    if (!nextParams) return false;
    m.set({ [paramsKey]: JSON.stringify(nextParams) });
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessoryType, cms]);

  const fetchBladeOrientations = useCallback(
    (autoSelect: boolean) => {
      if (accessoryType !== 'louver') return;
      const m = stagingModel.current;
      if (!m) return;
      const params = parseParams(m.get('louver_params'));
      const q: string[] = [];
      if (params.mfg_id) q.push(`mfg_id=${params.mfg_id}`);
      if (params.series_id) q.push(`series_id=${params.series_id}`);
      // ⚠ NO trailing slash, deliberately. Legacy's `fn.uni_tools.get` runs
      // `standardizeURL` (`universal_fn_tools.js:118-126`), which inserts the `/`
      // before the `?`, so legacy's WIRE url is `…louver_blade_orientations/?…`
      // while this one 301-redirects to it. Adding the slash here removes one
      // round trip — and measured on dev3 that made the reply beat
      // `loadSmartlist`'s 300 ms debounce at dialog OPEN, so the open Smartlist
      // query gained `blade_orientation_id=2&blade_orientation_name=Vertical`
      // that legacy's does not have (legacy's reply lands after the debounce).
      // The slower path is the one that matches the measured legacy ordering, so
      // the redundant 301 stays. See NOTES_FOR_OWNER.
      const url = `/api/v1.0/master/v1.0.0/ahu/louver_blade_orientations${q.length ? `?${q.join('&')}` : ''}`;
      void dispatch(accessorySelectorApi.endpoints.getLouverBladeOrientations.initiate(url))
        .unwrap()
        .then((orientations) => {
          setBladeOrientations(orientations);
          // ---- `_updateBladeOrientationSelect` :1910-1968, the params half ----
          // `orientations = self._louverBladeOrientations || cms.blade_orientation
          // || []` (:1919) always resolves to the freshly-fetched array here —
          // `[]` is truthy in JS, so an EMPTY fetch result does NOT fall back to
          // the CMS list. The `<select>`'s own option source stays where it was
          // measured (`AccessorySelectorBody`, the `comboMode && !series_id` rule).
          const list = Array.isArray(orientations) ? orientations : [];
          const fresh = parseParams(m.get('louver_params'));
          const currentId = (fresh.blade_orientation_id as number | string | undefined) || null;
          const isValid =
            currentId != null && list.some((o: any) => Number(o.id) === Number(currentId));
          let changed = false;
          // :1934-1945 — THE INVALIDATE-CLEAR. An orientation absent from the
          // freshly-fetched list is dropped in NON-combo mode. Combo keeps it on
          // purpose (KA-5250: an admin may have unchecked it in CMS, and
          // `_restoreComboLouverSelects` re-adds it as a disabled fallback).
          // ⚠ Legacy deletes only `blade_orientation_id` and leaves
          // `blade_orientation_name` behind — kept verbatim (it is observable:
          // legacy's post-pick Smartlist query carries `blade_orientation_name=
          // Vertical` with no `blade_orientation_id`).
          if (currentId != null && !isValid && !comboMode.current) {
            delete fresh.blade_orientation_id;
            changed = true;
          }
          // :1946-1956 — the sole-option auto-pick. Gated on `!isValid`, NOT on
          // "nothing selected yet": it also covers the selection that was just
          // invalidated above. Writes the NAME alongside the id, and does so
          // outside any combo check.
          if (!isValid && autoSelect && list.length === 1) {
            fresh.blade_orientation_id = list[0].id;
            fresh.blade_orientation_name = list[0].name;
            changed = true;
          }
          if (changed) {
            m.set({ louver_params: JSON.stringify(fresh) });
            // :1958 — the bounded second cascade pass. Synchronous, before the
            // bump, so the amended params are what any follow-up serialises.
            applyCascadeNow();
            bump();
            // :1962 — the freshly auto-picked identity is part of the aw_dims
            // query string, and no DOM `change` event fired for it.
            if (comboMode.current) void getComboEngine().fetchAwDims();
          }
        });
    },
    [accessoryType, dispatch, bump, applyCascadeNow, getComboEngine]
  );
  // publish the handle the smartlist-pick path above calls (see its comment).
  fetchBladeOrientationsRef.current = fetchBladeOrientations;

  const setImgComponent = useCallback(() => {
    if (accessoryType !== 'damper' && accessoryType !== 'louver') return;
    const m = stagingModel.current;
    if (!m || !cms) return;
    const params = parseParams(m.get(`${accessoryType}_params`));
    const r = resolveMountingImage(accessoryType, cms, params);
    setImgState({ src: r.src, label: r.label });
  }, [accessoryType, cms]);

  // -------------------------------------------------------------------------
  // `_syncManufacturerSeriesFilters` — the AUTO-PICK / INVALIDATE-CLEAR half.
  //
  // Legacy runs the whole function at the tail of `render()` (:383) and again
  // from `changeParamsField` (:2467), `_updateBladeOrientationSelect` (:1958)
  // and the combo smartlist pick (:1616). It patches the option classes AND may
  // silently amend `<type>_params`: the sole-option auto-pick (`:779-787`,
  // `:814-822`, `:852-860`) and the invalidate-clear (`:766-778`, `:802-813`,
  // `:840-851`).
  //
  // ★ The hide-set half is derived in the render body (pure, no state); THIS
  // effect is only the params amendment, run after commit so it mirrors legacy's
  // "render, then reconcile" order. Every branch is idempotent — an auto-picked
  // id is by construction inside its own allowed set, so the next pass returns
  // `nextParams: null` and it converges in one extra render.
  //
  // The write is silent (no `markChanged()`) exactly like legacy's
  // `set(…, {silent: true})`; the only follow-up legacy fires is
  // `_fetchComboAwDims()` when something changed in combo mode (:863) — the
  // freshly auto-picked identity is part of the aw_dims query string.
  useEffect(() => {
    if (accessoryType !== 'damper' && accessoryType !== 'louver') return;
    if (!seeded.current || !cms) return;
    // ★ THE BOUND. A handler that already ran the cascade synchronously
    // (`applyCascadeNow`) IS that state's single pass, and legacy renders nothing
    // afterwards — so skip the pass this commit would otherwise add. See
    // `applyCascadeNow` for the measured regression this prevents.
    if (cascadeDone.current) {
      cascadeDone.current = false;
      return;
    }
    const m = stagingModel.current;
    if (!m) return;
    const paramsKey = `${accessoryType}_params`;
    const current = parseParams(m.get(paramsKey));
    const { nextParams } = syncManufacturerSeriesFilters(
      cms,
      accessoryType,
      current,
      comboMode.current
    );
    if (!nextParams) return;
    m.set({ [paramsKey]: JSON.stringify(nextParams) });
    bump();
    if (comboMode.current) void getComboEngine().fetchAwDims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, cms, accessoryType]);

  // =========================================================================
  // FIELD HANDLERS (changeModelField / changeParamsField / onPositionField).
  // =========================================================================
  const onModelField = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const m = stagingModel.current;
      if (!m) return;
      const name = (e.target as any).name as string;
      let value: unknown =
        (e.target as any).type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
      // ---- legacy `changeModelField` :2314-2316 -------------------------------
      // `if (this.$el.find('input[data-type="number"][name="…"]').length)
      //    value = self.validateNumberField(…)` — a number field commits the
      // PARSED / ROUNDED / CLAMPED NUMBER, never the raw box text. This became
      // load-bearing with the display formatter this wave adds: editing a box
      // showing `20,000` would otherwise commit the string `"20,0001"`. Runs
      // BEFORE the combo enclosure clamp below, exactly as legacy orders them.
      if (
        (e.target as HTMLInputElement).dataset?.type === 'number' &&
        (e.target as any).type !== 'checkbox'
      ) {
        value = commitNumberValue(e.target as HTMLInputElement);
      }
      if (value === '') value = null;
      else if (name.endsWith('_id') && typeof value === 'string') value = parseInt(value, 10) || null;
      // ---- COMBO LOUVER — A1.3: the input-time CMS clamp (:2317-2337) --------
      // Clamp the enclosure W/H/D to the CMS range HERE so the 3-D preview and
      // the staging model never hold an out-of-range value (the server clamp is
      // only the backstop). Depth also honours the hard 12" min.
      // ⚠ R7: on dev3 EVERY `combo_*` CMS limit is NULL, so not one branch below
      // can fire there — implemented from the code, unexercisable on that DB.
      // ⚠ Q15: this must stay a plain local clamp. Legacy documents an infinite
      // loop (`change:enclosure_* → c_changed:affecting_view → getAccessoryData →
      // auto-size → render → clamp again`, :2970-2981) whenever the server's
      // computed size sits outside the CMS range, which is why the clamp is
      // applied SILENTLY and never re-emits a change event.
      if (
        comboMode.current &&
        value !== '' &&
        value != null &&
        (name === 'enclosure_depth' || name === 'enclosure_width' || name === 'enclosure_height')
      ) {
        const c = (cms ?? {}) as Record<string, unknown>;
        const lim =
          name === 'enclosure_depth'
            ? { min: Math.max(12, Number(c.combo_min_depth) || 0), max: c.combo_max_depth }
            : name === 'enclosure_width'
              ? {
                  min: c.combo_louver_enclosure_width_min,
                  max: c.combo_louver_enclosure_width_max,
                }
              : {
                  min: c.combo_louver_enclosure_height_min,
                  max: c.combo_louver_enclosure_height_max,
                };
        const entered = parseFloat(String(value).replace(/,/g, ''));
        let v = entered;
        if (!Number.isNaN(v)) {
          if (lim.min != null && v < Number(lim.min)) v = Number(lim.min);
          if (lim.max != null && v > Number(lim.max)) v = Number(lim.max);
          if (v !== entered) {
            value = v;
            // Legacy writes the clamped value straight back into the input
            // (:2334) BEFORE `_fetchComboAwDims()` runs — and that fetcher reads
            // the DOM, so without this write the aw_dims query would carry the
            // UNCLAMPED number. React re-renders the same value over it a moment
            // later, so the DOM poke is order-critical, not cosmetic.
            const el = document.querySelector<HTMLInputElement>(
              `#component_rating_mode input[name="${name}"]`
            );
            if (el) el.value = v.toFixed(2);
          }
        }
      }
      m.set({ [name]: value });
      // COMBO LOUVER — A1.3 (:2339-2355). `enclosure_material_id` lives on the
      // model, not in louver_params, and the API never echoes a display name for
      // it — cache the chosen option's text as material_id/material_name in
      // louver_params so a later CMS deactivation can still show a real name
      // (`_restoreComboLouverSelects`). Harmless that this second set() resets
      // hasChanged(): `enclosure_material_id` is not in CHANGEABLE_FIELDS.
      if (comboMode.current && name === 'enclosure_material_id') {
        const mp = parseParams(m.get('louver_params'));
        if (value === '' || value == null) {
          delete mp.material_id;
          delete mp.material_name;
        } else {
          const opt = (e.target as HTMLSelectElement).selectedOptions?.[0];
          mp.material_id = Number.isNaN(+String(value)) ? value : +String(value);
          mp.material_name = opt ? opt.text.trim() : null;
        }
        m.set({ louver_params: JSON.stringify(mp) });
      }
      markChanged();
      updateCanvasPreview();
      if (name === 'desired_face_velocity_actual' && value)
        localStorage.setItem(faceVelKey(modelId), String(value));
      // COMBO LOUVER — :2360-2362: re-evaluate the 101 % FV red rule as soon as
      // the DESIRED velocity moves (the actual one has not changed, but the
      // threshold has). Legacy applies this to every louver, combo or not; the
      // plain-louver case is a PRE-EXISTING React gap left untouched here — this
      // port only owns the combo path.
      if (comboMode.current && name === 'desired_face_velocity_actual') {
        getComboEngine().updateFaceVelocityColor();
      }
      if (CHANGEABLE_FIELDS[accessoryType].includes(name) && m.hasChanged(name)) {
        void getAccessoryData().then(() => {
          // 5e-sync — dev f64cf21357 (accessory.selector.js:1022-1026):
          // switching hood Style to 4-sided (style_id 4) autofocuses the
          // height_2 input once the round-trip lands.
          if (name === 'style_id' && (value as any) == 4) {
            document
              .querySelector<HTMLInputElement>('#component_rating_mode input[name="height_2"]')
              ?.focus();
          }
        });
      }
      // =====================================================================
      // COMBO LOUVER — A1.3: changeModelField's combo branch (:2380-2425).
      // ★ ORDER IS LOAD-BEARING. getAccessoryData() above was called FIRST but
      // suspends on awaitPendingLookups(), so the aw_dims GETs issued
      // synchronously below go out BEFORE its PATCH — which is exactly what the
      // measured logs show (§5.2(c)/(d): `GET aw_dims` at t=0, `PATCH
      // with_position` at t=0 but logged second).
      // =====================================================================
      if (comboMode.current) {
        const engine = getComboEngine();
        if (name === 'airflow_actual') {
          engine.updateFaceVelocity();
          // Auto-size ON (or never sized): airflow drives the sizing → re-run it.
          // OFF and already sized: keep the dimensions, recompute FV + APD only.
          if (engine.shouldAutoSize()) void engine.maybeAutoSize();
          else engine.recalc();
        } else if (name === 'enclosure_depth') {
          // Depth drives the sides-first sizing (the sides are capped by it), and
          // the SIDE aw_dims list is bounded by it, so refetch either way. The
          // new depth is already in the query because _fetchComboAwDims reads the
          // DOM input — no resize has to resolve first, which is why this path's
          // ordering is CORRECT (unlike (c)/(d)/(i)).
          void engine.fetchAwDims();
          if (engine.shouldAutoSize()) void engine.maybeAutoSize();
          else engine.recalc();
        } else if (name === 'enclosure_width' || name === 'enclosure_height') {
          // Spec: W/H may be custom-inputted. Persist the user's value and
          // recompute the actual FV. NEVER _maybeComboAutoSize() here — it would
          // overwrite the value just typed. (`_capComboEnclosureHeight` is
          // DEAD-6: both call sites are commented out in legacy, :2403/:2926.)
          void engine.fetchAwDims();
          engine.recalc();
        } else if (name === 'casing_mount_id') {
          // Casing mount feeds the clearance table the sizing uses, so it can
          // change the enclosure just like the depth does. BOTH halves are the
          // dev fixes: a092d3353c added the refetch, 56ff2152ec added the
          // _comboRecalc() — without which the enclosure never resizes AT ALL
          // while auto-size is off (Recess adds 3 × frame_depth = 12 → 51 → 63).
          // ★ MIRRORED DEFECT Q3: this refetch runs BEFORE the resize, so the
          // lists come back bounded by the OLD width (measured: bounded by 51
          // while the box became 63). Deliberately not fixed — parity-only.
          void engine.fetchAwDims();
          if (engine.shouldAutoSize()) void engine.maybeAutoSize();
          else engine.recalc();
        } else {
          void engine.maybeAutoSize();
        }
        // A1.4 — `self._applyComboValidation()` :2424, the LAST thing in legacy's
        // combo branch: the `.not-valid` pass over the enclosure depth/width/height
        // inputs + the face rows. Legacy ends it with `_updateSelectButtonState()`;
        // React pushes `#select` from an effect that runs after every render and
        // this handler ends in `bump()`, so the gate refreshes there.
        engine.applyValidation();
      }
      bump();
    },
    [accessoryType, cms, markChanged, updateCanvasPreview, modelId, getAccessoryData, getComboEngine, bump]
  );

  const onParamsField = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const m = stagingModel.current;
      if (!m) return;
      const name = (e.target as any).name as string;
      const value = e.target.value;
      const paramsKey = `${accessoryType}_params`;
      const params = parseParams(m.get(paramsKey));
      if (value === 'None' || value === '') delete params[name];
      else params[name] = Number.isNaN(+value) ? value : +value;
      // COMBO LOUVER — A1.2 (:2442-2452): cache the chosen option's display text
      // alongside its id, so a saved selection still shows a real name if CMS
      // later deactivates it (`_restoreComboLouverSelects` :624-634). The four
      // shared-identity selects are the whole catalog identity for all 3 faces.
      if (comboMode.current) {
        const nameParam = COMBO_NAME_PARAM[name];
        if (nameParam) {
          if (value === 'None' || value === '') delete params[nameParam];
          else {
            const opt = (e.target as HTMLSelectElement).selectedOptions?.[0];
            params[nameParam] = opt ? opt.text.trim() : null;
          }
        }
      }
      m.set({ [paramsKey]: JSON.stringify(params) });
      markChanged();
      updateCanvasPreview();
      paramsChangedAutoPick.current = true;
      if (name === 'mfg_id' || name === 'series_id' || name === 'blade_orientation_id') {
        if ((name === 'mfg_id' || name === 'series_id') && accessoryType === 'louver')
          fetchBladeOrientations(true);
        // ★ :2467 — `_syncManufacturerSeriesFilters()` runs SYNCHRONOUSLY here,
        // BEFORE the `getAccessoryData()` save below (:2489). The effect keyed to
        // the render counter is one render too late for this path: it commits the
        // auto-pick after the PATCH body has already been serialised, so the
        // sole-option `frame_mtl_id`/`frame_mtl_name` reached the DOM but never
        // the server. Measured on dev3 (real pick, mfg 47): legacy PATCHed
        // `{…mfg_id:47, mfg_name:"Greenheck", frame_mtl_id:4503,
        //   frame_mtl_name:"Aluminum-Extruded-Mill"}` while React PATCHed
        // `{…mfg_id:47, mfg_name:"Greenheck"}` and never followed up.
        // Applying it here mirrors legacy's ordering exactly; the effect still
        // covers legacy's render tail (:383).
        // ⚠ And it is legacy's ONLY pass for this state — `applyCascadeNow` sets
        // `cascadeDone` so the effect does not add a second one. That second pass
        // used to observe the transient "series just cleared, orientation not yet
        // invalidated by the in-flight fetch" state and auto-pick a series legacy
        // never picks; see `applyCascadeNow`'s note.
        applyCascadeNow();
        setImgComponent();
      }
      loadSmartlist(); // Q12: a no-op in combo mode (the combo list is static).
      if (name === 'aw_width' || name === 'aw_height') void getAccessoryData();
      // A damper's louvers only EXIST in `mounting_planes` once the server knows
      // which catalogue to look in: `AHUAccessoryDamper.mountable_louvers` asks
      // for the catalogue damper at the louver's airway size, and
      // `damper_mounting.catalogue_damper` returns nothing until manufacturer,
      // series and frame material are all chosen. That list is only recomputed by
      // this round trip, and no other call site fires on these three — so picking
      // them left the Mounting Plane select showing the louver-less list the
      // dialog opened with, however long the user waited or how often the page was
      // reloaded (the identity is not saved until Select, so a reload brought the
      // same empty params back).
      if (
        accessoryType === 'damper' &&
        (name === 'mfg_id' || name === 'series_id' || name === 'frame_mtl_id')
      ) {
        void getAccessoryData();
      }
      // COMBO LOUVER — A1.2/A1.3: changeParamsField's combo branch (:2482-2494).
      // Sync the position (mounting plane) to the server + staging model so the
      // sides-first auto-size can run right after picking mfg/series, without
      // needing an air-direction change to do the sync; the 350 ms debounce in
      // maybeAutoSize lets that round trip land first.
      if (comboMode.current) {
        const engine = getComboEngine();
        if (
          name === 'mfg_id' ||
          name === 'series_id' ||
          name === 'blade_orientation_id' ||
          name === 'frame_mtl_id'
        ) {
          void getAccessoryData();
          void engine.fetchAwDims();
        }
        void engine.maybeAutoSize();
      }
      bump();
    },
    [accessoryType, cms, markChanged, updateCanvasPreview, fetchBladeOrientations, applyCascadeNow, setImgComponent, loadSmartlist, getAccessoryData, getComboEngine, bump]
  );

  const onPositionField = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const sp = stagingPosition.current;
      const m = stagingModel.current;
      if (!sp || !m) return;
      const tag = (e.target as any).tagName;
      if (tag === 'INPUT') {
        let name = (e.target as any).name as string;
        if ((e.target as HTMLInputElement).type === 'checkbox') {
          sp.set({ [name]: (e.target as HTMLInputElement).checked });
        } else {
          if (/position_./.test(name)) name = name[name.length - 1];
          sp.set({ [name]: e.target.value });
        }
        markChanged();
        void getAccessoryData();
      } else {
        // SELECT — mounting_plane_id.
        const sel = e.target as HTMLSelectElement;
        const opt = sel.options[sel.selectedIndex];
        const name = sel.name;
        let value: any = sel.value;
        if (name.includes('_id')) value = parseInt(value, 10) || null;
        const updates: Record<string, unknown> = { [name]: value };
        if (name === 'mounting_plane_id') {
          mountingPlaneIdChangedRef.current = true;
          const prev = sp.get('mounting_plane_id');
          if ((prev === 9) !== (value === 9)) {
            checkSelectsRef.current = true;
            m.set({ [`master_${accessoryType}_id`]: null, spn: null });
          }
          if (value === 9) m.set({ casing_mount_id: 1 });
          if (opt?.dataset.position) {
            updates.mounting_type = opt.dataset.position;
            (sp as any).mountingType = opt.dataset.position;
          }
          if (accessoryType === 'damper') {
            const dv = opt?.dataset.value;
            // Hood and Louver entries carry the accessory's own id as the
            // option value; a damper has one mounting plane, so picking either
            // clears the other.
            m.set({
              hood_id: dv === 'hood' ? value : null,
              louver_id: dv === 'louver' ? value : null,
            });
            // A damper mounted on a louver takes the louver's airway size. The
            // save forces it server-side either way, but the panel's Airway
            // W x H reads the staged params, so without this the user picks a
            // louver and keeps looking at the size the damper had before.
            // `master_damper_id` and the two dimensions ride along on the
            // louver's own entry in `mounting_planes`.
            if (dv === 'louver') {
              const entry = ((m.get('mounting_planes') as PlaneRow[] | undefined) ?? []).find(
                (p) => String(p.value ?? '') === 'louver' && Number(p.id) === value
              ) as (PlaneRow & { master_damper_id?: number; aw_width?: number; aw_height?: number }) | undefined;
              if (entry?.master_damper_id) {
                const staged = parseParams(m.get(`${accessoryType}_params`));
                m.set({
                  master_damper_id: entry.master_damper_id,
                  [`${accessoryType}_params`]: JSON.stringify({
                    ...staged,
                    aw_width: entry.aw_width,
                    aw_height: entry.aw_height,
                  }),
                });
                // …and re-run the catalogue query, the way `changeParamsField`
                // does for every other write to `<type>_params`. The airway the
                // louver forces is a new filter, and the damper it forces is a
                // DIFFERENT catalogue row: without this the smartlist keeps the
                // rows and the `n / N` count of the size the damper had before,
                // and since the selected row is marked by `master_damper_id`,
                // the damper the louver just picked is in no list and shows as
                // selected nowhere until the dialog is reopened.
                loadSmartlist();
              }
            }
          }
          // Mirror the pick onto the Mounting tab's popup state too (the
          // opposite direction of the fix above): the popup's plan-view
          // image only reads `mountingModel`/`posAdapter`, so choosing a
          // plane from THIS dropdown left the image showing the previously
          // selected wall/floor/roof. On-cabinet only — the popup has no
          // internal-wall representation (AccessoryMountingPopup is
          // on_cabinet-only), so an internal-wall pick here has nothing to
          // mirror. Updating `posAdapter` directly (not just `mounting`)
          // keeps it in lockstep with `sp` so the deferred sync-back above
          // doesn't immediately clobber this fresher value with its own
          // (unrelated) stale `mounting_plane_id`.
          if (opt?.dataset.position === 'on_cabinet') {
            const planeValue = opt?.dataset.value ? String(opt.dataset.value) : null;
            stagingPosAdapter.current?.set({ mounting_plane_id: value });
            mountingModel.current?.set({ plane_id: value, plane_value: planeValue });
          }
        }
        sp.set(updates);
        markChanged();
        void getAccessoryData();
      }
      // COMBO LOUVER — A1.3: onPositionFieldChange's combo tail (:2920-2928).
      // A mounting-plane / distance change alters the clearances the sizing uses.
      // (`_capComboEnclosureHeight()` is commented out in legacy here too —
      // DEAD-6 — so the enclosure top MAY protrude above the section airway.)
      if (comboMode.current) void getComboEngine().maybeAutoSize();
      bump();
    },
    [accessoryType, markChanged, getAccessoryData, getComboEngine, loadSmartlist, bump]
  );

  const onAutoSize = useCallback(() => {
    const m = stagingModel.current;
    // COMBO LOUVER — A1.3: `autoSize` :2503-2506 —
    // `if (self._comboMode) { self._comboAutoSize(); return; }`. The button
    // bypasses BOTH the toggle and the 350 ms debounce and never checks
    // _comboReady(); it is the one unconditional path to a full sides-first
    // resize. (Wave 1 already greys it + kills pointer-events while the toggle is
    // ON, per tmpl:230, so it is reachable only with auto-size OFF.)
    if (comboMode.current) {
      if (!m) return;
      markChanged();
      void getComboEngine().autoSize();
      bump();
      return;
    }
    const shim = getShim();
    if (!m || !shim) return;
    const fv =
      String(m.get('desired_face_velocity_actual') ?? '').replace(',', '') ||
      String(shim.defaultFaceVelocity);
    const data: Record<string, unknown> = { airflow_actual: m.get('airflow_actual') };
    const changed = shim.autoSize?.(true, m as any, data, Number(fv));
    if (changed) m.set(data);
    markChanged();
    void getAccessoryData();
    bump();
  }, [getShim, markChanged, getAccessoryData, getComboEngine, bump]);

  // =========================================================================
  // COMBO LOUVER — A1.3: `toggleComboAutoSize` :1810-1830. ★ THE ORDERING FIX
  // (dev commit 3135165b0d) IS THIS HANDLER.
  //  · ON  → the resize can change enclosure W/H/D and the aw_dims lists are
  //          bounded differently for `auto_size=true`, so the refetch is CHAINED
  //          off the promise: `maybeAutoSize().then(() => fetchAwDims())`. Both
  //          GETs therefore issue only after the PATCH response lands (measured
  //          §5.2(b): PATCH at 0 ms → 200 at 168 ms → both GETs at 168 ms).
  //  · OFF → nothing touches W/H/D, so there is nothing to wait for: refetch
  //          immediately, and NO /with_position fires at all (measured §5.2(a)).
  //          The front width list collapses 277 → 38 because `auto_size=false`
  //          makes the front bound come from the request's own `&width=`.
  // The re-render is what enables/disables the three face W×H pairs and the
  // enclosure W/H inputs (D is always enabled).
  // =========================================================================
  const onComboAutoSizeToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const m = stagingModel.current;
      if (!m || !comboMode.current) return;
      const engine = getComboEngine();
      comboAutoSizeEnabled.current = e.target.checked;
      m.set({ auto_size: comboAutoSizeEnabled.current });
      // legacy sets `auto_size` SILENTLY; React must still arm the userArmed gate
      // or the /with_position preview below could not fire at all. The toggle is a
      // genuine user edit whose result Select must persist, so arming is correct.
      markChanged();
      bump(); // = render()
      if (comboAutoSizeEnabled.current) {
        void engine.maybeAutoSize().then(() => {
          void engine.fetchAwDims();
        });
      } else {
        void engine.fetchAwDims();
      }
    },
    [getComboEngine, markChanged, bump]
  );

  // =========================================================================
  // COMBO LOUVER — A1.4: the face-row handlers + Reset. All three are thin
  // adapters onto the engine (legacy's handlers are View methods, so the whole
  // body lives there); the engine owns the ordering, the guards and the writes.
  // =========================================================================
  /** `toggleComboFace` :1832-1872 — the three face checkboxes. The engine
   * re-renders WITHOUT changing state when the last checked face is the one being
   * unchecked, which is legacy's `$cb.prop('checked', true); return;`. */
  const onComboFaceToggle = useCallback(
    (face: ComboFace, checked: boolean) => {
      if (!comboMode.current) return;
      getComboEngine().toggleFace(face, checked);
    },
    [getComboEngine]
  );

  /** `onComboFaceFieldChange` :1131-1159 — one face's W or H select. The ONLY
   * sender of `edited_face`, i.e. the only path on which the server may grow the
   * enclosure (front → width, left/right → depth). */
  const onComboFaceField = useCallback(
    (face: ComboFace, name: 'combo_aw_width' | 'combo_aw_height', raw: string) => {
      if (!comboMode.current) return;
      getComboEngine().onFaceField(face, name, raw);
    },
    [getComboEngine]
  );

  /** `resetComboFace` :1512-1541 — ⚠ leaves APD stale and makes the NEXT
   * `aw_dims` 500 (both mirrored; see the engine's `reset`). */
  const onComboReset = useCallback(() => {
    if (!comboMode.current) return;
    getComboEngine().reset();
  }, [getComboEngine]);

  // COMBO LOUVER — A1.5: `openComboLouverHowTo` :3374-3391 / `closeComboLouverHowTo`
  // :3393-3396. Clicking the menu while the popup is already `.active` CLOSES it,
  // so this is a toggle; legacy also closes it from `cancelChanges` (:2933), which
  // here is the component unmounting with the dialog.
  const onComboHowTo = useCallback(() => setHowToOpen((v) => !v), []);

  // COMBO LOUVER — A1.2: `showComboSmartlist` :1576-1581 / `closeComboSmartlist`
  // :1560-1570. ONE shared smartlist for the whole combo (the identity is shared;
  // only the sizes are per-face). Toggling closes it, and closing returns to the
  // 'ahu' 3-D tab — NOT to 'mounting', where the plain-louver toggle returns.
  const onComboSmartlistToggle = useCallback(() => {
    setTab(dataTabRef.current === 'smartlist' ? 'ahu' : 'smartlist');
  }, []);

  // COMBO LOUVER — A1.2: `selectFromSmartlist` :3316-3319 routes a combo row to
  // `_applyComboSmartlistPick`, which sets the SHARED identity for all three
  // faces. There is no per-face master_louver_id and no `_applySmartlistItem`.
  const onComboSmartlistRow = useCallback(
    (row: Record<string, unknown>) => {
      void getComboEngine().applySmartlistPick(row);
    },
    [getComboEngine]
  );

  // =========================================================================
  // SELECT / CANCEL (chassis fan-out) — CODE-ONLY apply.
  // =========================================================================
  const selectAccessory = useCallback(async () => {
    const m = stagingModel.current?.toJSON();
    const position = stagingPosition.current?.toJSON();
    const shim = getShim();
    if (!m || !position || !shim) return;
    const paramsKey = `${accessoryType}_params`;
    const modelChanges: Record<string, unknown> = {};
    const positionChanges: Record<string, unknown> = {};
    MODEL_FIELDS.forEach((f) => {
      if (m[f] !== init.current[f]) modelChanges[f] = m[f] != null && m[f] !== '' ? m[f] : null;
    });
    if (m[paramsKey] !== init.current[paramsKey]) modelChanges[paramsKey] = m[paramsKey];
    POSITION_FIELDS.forEach((f) => {
      if (position[f] !== (initPosition.current ?? {})[f])
        positionChanges[f] = position[f] != null && position[f] !== '' ? position[f] : null;
    });
    if (Object.keys(modelChanges).length) {
      // ---- Arm the board model so Select actually persists --------------------
      // Legacy's board row has no data-safety gate, so `current_model.set(
      // modelChanges)` runs the row's own debounced auto-save and produces the
      // real `PATCH /ahu/<plural>/<id>`. React's FormModel is userArmed-gated
      // (autoSaveModel.ts:283) and a selector session never armed the BOARD
      // model — so for every type except combo the dialog's entire work was
      // silently discarded: the only wire call was `/with_position`, the PREVIEW
      // endpoint, the row repainted with the new values, and a reload showed it
      // blank again. Measured on damper / hood / opening / plain louver.
      //
      // Pressing Select IS the user gesture the gate exists to wait for — the
      // gate's own doc block lists "rating commit" among the gestures that flip
      // it. Arming here is the NARROW fix: this line already sits inside
      // `if (Object.keys(modelChanges).length)`, so Cancel and no-op Selects
      // still never arm, and a fresh page gives a fresh unarmed model per row.
      //
      // ⚠ Do NOT "fix" this by widening the shared gate instead. It exists
      // because merely RENDERING a form used to write data (6e2a-3: the
      // eliminator mount auto-pick corrupted a real row, `8584bbdd1a`), and
      // legacy's ungated equivalent is actively destructive — measured on
      // :8130, inserting an unrelated opening rewrote a *configured* damper's
      // stored 68x34 to 66x33 because the section airway had changed.
      //
      // Precedent: the sibling position path already does exactly this,
      // unconditionally — `useAccessoryForm.ts` `applyPositionDiff()` calls
      // `pos.markUserAction(); pos.set(diff);`, which is why the position side
      // of Select has always persisted while the model side did not.
      shim.model.markUserAction();
      shim.model.set(modelChanges);
      if ('tag' in modelChanges)
        // ⚠ `String(modelChanges.tag)` turned a CLEARED tag into the four-letter
        // string "null": the empty tag box normalises to `null` two lines above,
        // `'tag' in modelChanges` is then true, and `TabAccessories`' handler
        // assigns the payload straight onto `row.tag` — so every Select on an
        // untagged accessory repainted the board row as `Combo Louver : null`
        // (measured post-Select; legacy shows `Combo Louver`). Legacy passes the
        // raw value (`Dispatcher.trigger('update:accessories:tag:onboard',
        // { id, tag: modelChanges.tag })`, accessory.selector.js:2791-2796) and
        // its `{{tag}}` renders empty, so coalesce to '' rather than stringifying.
        ahuBoardBus.emit('update:accessories:tag:onboard', {
          id: modelId,
          tag: modelChanges.tag == null ? '' : String(modelChanges.tag),
        });
    }
    let shouldCreatePosition = false;
    // A louver pick is decided by the louver itself, not by whether the plane
    // changed: when the damper already sits on the louver's wall the preview
    // answers with the same plane, there is no diff, and the mount branch (the
    // on_cabinet position swap and the count/field refreshes) would be skipped.
    const pickedLouverId = modelChanges.louver_id ? Number(modelChanges.louver_id) : null;
    if (pickedLouverId) {
      shouldCreatePosition = true;
      await shim.createPosition('on_cabinet', pickedLouverId, 'louver', true, {});
    } else if (positionChanges.mounting_plane_id) {
      // resolve the option's data-position / data-value off the rendered select.
      const optSel = document.querySelector(
        `select[name="mounting_plane_id"] option[value="${positionChanges.mounting_plane_id}"]`
      ) as HTMLOptionElement | null;
      const newMountingType = optSel?.dataset.position;
      const newMountingValue = optSel?.dataset.value;
      if (newMountingType) {
        shouldCreatePosition = true;
        if ((positionChanges.x as number) < 0) delete positionChanges.x;
        if ((positionChanges.y as number) < 0) delete positionChanges.y;
        await shim.createPosition(
          newMountingType,
          positionChanges.mounting_plane_id as number,
          newMountingValue,
          true,
          positionChanges
        );
        const prevPlane = (initPosition.current ?? {}).mounting_plane_id;
        const newPlane = positionChanges.mounting_plane_id;
        if ((prevPlane === 9) !== (newPlane === 9)) {
          ahuBoardBus.emit('damper:fan_intake:crossing', sectionId, modelId);
        }
      }
    }
    if (position.default_pos_x || (positionChanges.x as number) < 0) delete positionChanges.x;
    if (position.default_pos_y || (positionChanges.y as number) < 0) delete positionChanges.y;
    if (!shouldCreatePosition && Object.keys(positionChanges).length) {
      // ★★ COMBO LOUVER — A1.6 / §4 W3 STEP 1: THE POSITION PATCH GOES FIRST.
      // `selectAccessory` :2834 — `if (self.current_model.get('is_combo'))
      // self.current_model.position.save(positionChanges, {patch: true}); else
      // self.current_model.position.set(positionChanges);` — i.e. a combo
      // IMMEDIATELY PATCHes `/api/v1.0/ahu/positions/<id>` (the generic route, not
      // `on_cabinet`) instead of relying on the position model's own debounced
      // save, so the row is re-centred before the louver PATCH lands. Measured
      // body: `{"mounting_type":null}`. NOT awaited (legacy does not await either),
      // so it is merely ISSUED first — which is what the measured order shows.
      if (comboMode.current) {
        const positionId = Number(position.id ?? modelId);
        void dispatch(
          ahuBoardFormsApi.endpoints.patchAhuPosition.initiate({
            id: positionId,
            data: positionChanges,
          })
        );
      } else {
        // Item 4 fix — plane UNCHANGED, x/y/mounting_type changed: legacy does
        // `current_model.position.set(positionChanges)` (accessory.selector.js
        // :1177-1179), which auto-PATCHes ahu/positions[/<base>]/<id> with the
        // diff. The shim's applyPositionDiff arms + sets the LIVE position model
        // so the identical PATCH fires (was a no-op — the diff was silently
        // dropped, 6e2c-3 F2 data-loss gap).
        shim.applyPositionDiff?.(positionChanges);
      }
    }
    // ★★ COMBO LOUVER — A1.6 / §4 W3 STEP 2: THE PERSIST (:2848-2851).
    // `if (self.accessoryType === 'louver') { if (self._comboMode) await
    // self._persistCombo(); else if (is_combo) PATCH {is_combo:false}; }`.
    // The `else if` branch (combo → plain) is DEAD-1: `_comboMode` is computed once
    // from `is_combo` and nothing anywhere turns combo mode off, so
    // `_comboMode === false && is_combo === true` is unreachable. NOT ported (W6).
    if (accessoryType === 'louver' && comboMode.current) {
      const saved = await getComboEngine().persist();
      if (saved) {
        // :2701-2712 — the server row is authoritative (`partial_update`
        // re-serializes after `recenter_combo_enclosure`, unlike POST/PUT — R11).
        // SILENT: this is the response echo, not a user edit, so it must not
        // re-enter the board row's auto-save pipeline.
        shim.model.set(
          {
            is_combo: true,
            auto_size: saved.auto_size,
            enclosure_width: saved.enclosure_width,
            enclosure_height: saved.enclosure_height,
            enclosure_depth: saved.enclosure_depth,
            enclosure_material_id: saved.enclosure_material_id,
            combo_faces: saved.combo_faces,
            face_velocity_actual: saved.face_velocity_actual,
            apd: saved.apd,
          },
          { silent: true }
        );
      }
    }
    shim.render();
    ahuBoardBus.emit('rating_mode:hide');
    preview.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessoryType, getShim, modelId, sectionId, preview, dispatch, getComboEngine]);

  const cancelChanges = useCallback(() => {
    preview.dispose();
    // left-panel reverse (legacy cancelChanges).
    document.querySelector('.main-rotation')?.setAttribute('data-accessory', '');
    document.querySelector(`label[for="accessory_activity_${modelId}"]`)?.classList.remove('open');
    const activity = document.querySelector<HTMLInputElement>(`#accessory_activity_${modelId}`);
    if (activity?.checked) activity.click();
    const form = document.querySelector(`#forms_accessory_${modelId}`);
    form?.querySelector<HTMLElement>('input[data-subtab="mounting"]:checked + label i')?.click();
    form?.querySelector<HTMLElement>('input[data-subtab="performance"]:checked + label i')?.click();
    const shim = getShim();
    if (shim) (shim as any).selectionDialogView = null;
  }, [modelId, getShim, preview]);

  // =========================================================================
  // LEFT-PANEL DRIVE on open (legacy initialize :67-103) + chassis fan-out.
  // =========================================================================
  useEffect(() => {
    document.querySelector('.main-rotation')?.classList.add('hide-shadow');
    document.querySelector('.main-rotation')?.setAttribute('data-accessory', String(modelId));
    document.querySelector(`label[for="accessory_activity_${modelId}"]`)?.classList.add('open');
    const activity = document.querySelector<HTMLInputElement>(`#accessory_activity_${modelId}`);
    if (activity && !activity.checked) activity.click();
    const timer = setTimeout(() => {
      const form = document.querySelector(`#forms_accessory_${modelId}`);
      document.querySelector<HTMLElement>('#show_accessories')?.click();
      form?.querySelector<HTMLElement>('input[data-subtab="mounting"]:not(:checked) + label i')?.click();
      form?.querySelector<HTMLElement>('input[data-subtab="performance"]:not(:checked) + label i')?.click();
    }, 200);
    return () => clearTimeout(timer);
  }, [modelId]);

  // chassis select/cancel fan-out + section/IW re-render.
  useEffect(() => {
    const selKey = `${accessoryType}_${modelId}:selector:select` as `${string}_${number}:selector:select`;
    const cancelKey = `${accessoryType}_${modelId}:selector:cancel` as `${string}_${number}:selector:cancel`;
    const offs = [
      ahuBoardBus.on(selKey, () => void selectAccessory()),
      ahuBoardBus.on(cancelKey, () => cancelChanges()),
      ahuBoardBus.on('cancel:dialog:changes', () => cancelChanges()),
      ahuBoardBus.on('internal_wall:created', () => bump()),
      ahuBoardBus.on('internal_wall:deleted', () => bump()),
      ahuBoardBus.on('internal_wall:change', () => bump()),
    ];
    return () => offs.forEach((off) => off());
  }, [accessoryType, modelId, selectAccessory, cancelChanges, bump]);

  // =========================================================================
  // MOUNTING TAB — build the staging-backed adapters + MountingModel (REUSE).
  // =========================================================================
  useEffect(() => {
    if (!seeded.current || !cms || stagingForm.current) return;
    const sm = stagingModel.current!;
    const sp = stagingPosition.current!;
    const formAdapter = new StagingFormAdapter(sm);
    const posAdapter = new StagingPositionAdapter(sp.toJSON(), sp.get('mounting_type') as string);
    stagingForm.current = formAdapter;
    stagingPosAdapter.current = posAdapter;
    const dims = {
      aw_width: Number(section.aw_width) || 0,
      aw_height: Number(section.aw_height) || 0,
      aw_length: Number(section.aw_length) || 0,
    };
    mountingModel.current = buildStagingMounting(
      formAdapter,
      posAdapter,
      () => dims,
      () => (cms.mounting_plane as any[]) ?? []
    );
    // Plane/distance picks made through the popup's plan-view image
    // (mounting.set + recalc_position, in AccessoryMountingPopup) land on
    // `posAdapter` — a CLONE of `sp` taken above, when this effect ran. They
    // never made it back onto `sp` itself, which is what the "Mounting Plane"
    // dropdown / distance rows (`position={sp.toJSON()}` below) render from —
    // so picking a plane on the image left the dropdown showing the stale
    // prior plane. Worse, the ORIGINAL write-back here merged
    // `mountingModel.toJSON()` (`{plane_id, plane_value, ...distances}`) onto
    // `sp`, which reads `mounting_plane_id`/`x`/`y` — different key names
    // entirely, so even the debounced sync never actually landed.
    //
    // Mirror `posAdapter`'s snapshot (the correctly-keyed `{mounting_plane_id,
    // x, y, ...}` recalc_position() already wrote) onto `sp` instead, and do
    // it as soon as the current synchronous call stack (mounting.set() +
    // confirm()'s recalc_position(), both synchronous) finishes — a bare
    // setTimeout(…, 0), not the 1s debounce below, which exists only to
    // throttle the getAccessoryData() recompute.
    mountingModel.current.subscribe(() => {
      setTimeout(() => {
        sp.set({ mounting_type: null, mounting_plane: null, ...posAdapter.toJSON() });
        bump();
      }, 0);
      if (positionDebounce.current) clearTimeout(positionDebounce.current);
      positionDebounce.current = setTimeout(() => {
        markChanged();
        void getAccessoryData();
      }, 1000);
    });
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cms]);

  // mini-3D canvas attach when AHU tab visible. The host element only mounts
  // AFTER the cms/staging loading gate clears (the component returns a preloader
  // first), so a dataTab-only effect would fire while ahuHostRef is still null.
  // A callback ref attaches the instant the host node mounts (and re-runs on
  // dataTab return because the node unmounts/remounts with the wrapper).
  const setAhuHost = useCallback(
    (node: HTMLDivElement | null) => {
      ahuHostRef.current = node;
      if (node && dataTab === 'ahu') {
        preview.attach(node);
        updateCanvasPreview();
      }
    },
    [dataTab, preview, updateCanvasPreview]
  );
  // re-attach + redraw whenever the AHU tab becomes visible again.
  useEffect(() => {
    if (dataTab === 'ahu' && ahuHostRef.current) {
      preview.attach(ahuHostRef.current);
      updateCanvasPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTab]);

  // teardown timers (incl. the combo engine's two separate 350 ms debounces).
  useEffect(
    () => () => {
      if (smartlistTimer.current) clearTimeout(smartlistTimer.current);
      if (positionDebounce.current) clearTimeout(positionDebounce.current);
      if (tagFocusTimer.current) clearTimeout(tagFocusTimer.current);
      comboEngineRef.current?.dispose();
    },
    []
  );

  // ---- COMBO LOUVER — keep the engine's ctx bridge fresh -------------------
  // The engine is created ONCE (legacy: one View instance) but reaches these
  // through getters, so they must point at the current identities every render.
  cmsRef.current = cms;
  comboCbs.current.getAccessoryData = getAccessoryData;
  comboCbs.current.updateCanvasPreview = updateCanvasPreview;
  comboCbs.current.setImgComponent = setImgComponent;

  if (!seeded.current || !cms || !stagingModel.current) {
    return <div className="preloader dotted-preloader active" />;
  }

  const model = stagingModel.current.toJSON();
  const position = stagingPosition.current!.toJSON();
  const params = parseParams(model[`${accessoryType}_params`]);
  const planeValue = mountingPlaneValue(cms, position.mounting_plane_id);
  const plane = resolvePlane(cms, position.mounting_plane_id);
  // The CMS carries the cabinet planes; a damper can also be mounted on a
  // louver in its section, and those entries only exist on the damper itself
  // (its `mounting_planes`, refreshed by every staged round trip, so they
  // arrive as soon as manufacturer / series / frame material are picked).
  // Without this the louvers show up in the row's Mounting tab but not here,
  // where the damper is being selected in the first place.
  const modelPlanes = (model.mounting_planes as PlaneRow[] | undefined) ?? [];
  // Fan Intake is a FAN-section plane, and the CMS list is per-manufacturer —
  // it knows nothing about the section this damper sits in, so on its own it
  // offers Fan Intake in every section. The damper's own `mounting_planes`
  // carries the server's answer (`AHUAccessoryDamper.mounting_planes`: the
  // section is a fan section, its fan is selected, and this damper is the one
  // holding the intake), which is also what the row's Mounting tab lists.
  // Legacy fed this same select the model's list, not the CMS one
  // (damper.selector.tmpl:243 `{{#each mounting_planes}}` ← damper/view/form.js
  // dataGetterForRendering).
  const fanIntakeAllowed = modelPlanes.some((p) => String(p.value ?? '') === 'fan_intake');
  const planesCab = [
    ...planesOnCabinet(cms).filter(
      (p) =>
        accessoryType !== 'damper' ||
        fanIntakeAllowed ||
        String(p.value ?? '') !== 'fan_intake'
    ),
    ...(accessoryType === 'damper'
      ? modelPlanes.filter((p) => String(p.value ?? '') === 'louver')
      : []),
  ];
  // `_syncManufacturerSeriesFilters`' `toggleClass('hide', …)` half — the three
  // identity selects render the full cms lists and hide what the cascade
  // disallows. The `nextParams` half is applied by the effect above.
  const paramsCascade = syncManufacturerSeriesFilters(cms, accessoryType, params, comboMode.current);
  const awDims = getAvailableAirwayDimensions(
    planeValue,
    { aw_width: section.aw_width, aw_height: section.aw_height, aw_length: section.aw_length },
    position.mounting_plane_id
  );

  // hood.selector.tmpl:622-643 — the "Hood" tab's typical-model icon + label.
  // There, `model.style` is a STRING value ('hood_4_sided', …) matched
  // against literal `icon-hood_*` classes, and `style_name` is the matching
  // cms.style row's display name (accessory.selector.js:356-360, `==` loose
  // match on id). React's model only carries `style_id` (numeric), so
  // resolve both off that same cms.style row.
  const hoodStyleRow =
    accessoryType === 'hood'
      ? ((cms.style as Array<Record<string, unknown>> | undefined) ?? []).find(
          (s) => String(s.id) === String(model.style_id)
        )
      : undefined;
  const hoodStyleValue = hoodStyleRow ? String(hoodStyleRow.value ?? '') : '';
  const hoodStyleName = hoodStyleRow ? String(hoodStyleRow.name ?? '') : '';

  // staging-backed mounting api (for the reused popup, inlineContainer mode).
  const mountingApi: any =
    mountingModel.current && stagingPosAdapter.current && stagingForm.current
      ? {
          model: stagingForm.current,
          position: stagingPosAdapter.current,
          mounting: mountingModel.current,
          mountingPlanes: (cms.mounting_plane as any[]) ?? [],
          awDims: {
            aw_width: Number(section.aw_width) || 0,
            aw_height: Number(section.aw_height) || 0,
            aw_length: Number(section.aw_length) || 0,
          },
          popupOpen: dataTab === 'mounting',
          closeMountingPopup: () => undefined,
          createPosition: async (positionType: string, planeId: number) => {
            stagingPosAdapter.current!.set({
              mounting_plane_id: planeId,
              mounting_type: positionType,
            });
            stagingPosition.current!.set({ mounting_plane_id: planeId, mounting_type: positionType });
            mountingModel.current.setPosition(stagingPosAdapter.current);
            markChanged();
            return undefined;
          },
          cmsAccessory: cms,
        }
      : null;

  return (
    /* ★ THE UNCLASSED VIEW ROOT — load-bearing, do NOT flatten this into a
       fragment. Legacy renders the whole accessory selector into a Backbone
       view `el` (a plain `<div>`) that sits between
       `#rating_mode_<type>_content` and both `#coil_new` and
       `.mounting_popup_wrapper` (measured legacy chain: content 997 → div 922 →
       #coil_new 922, with the popup wrapper a SIBLING of #coil_new inside that
       div). React used a fragment, so `#coil_new` AND the popup wrapper were
       both DIRECT children of the content div — and once that div carries
       `rating_mode_accessory_content` (the occlusion fix), the rule
       `.ahu-rate-content-item .rating_mode_accessory_content > div { height:100% }`
       (rating_mode.scss:2192) matches the POPUP WRAPPER too. At specificity
       (0,2,1) it beats `.mounting_popup_wrapper[data-zoom="zoom-in"]
       { height: calc(100% - 420px) }` (0,2,0), and because the wrapper is
       `position:absolute` the 100% resolved against `#component_rating_mode`
       (1037px) instead of 617px — a 420px-too-tall popup whose 3-D canvas then
       covered the entire `.col-xs-7` column (Mounting Plane, all four distance
       rows, Blade Direction and, for a combo, the whole Enclosure block were
       unreachable by `elementFromPoint`). This div restores legacy's nesting so
       the wrapper matches only the zoom rule again. `#coil_new` keeps its own
       height from `.coil-rating-mode { height: calc(100vh - 280px) }`, so it is
       unaffected by losing the `> div` match. */
    <div>
      <AccessoryLeftPanel
        type={accessoryType}
        cms={cms}
        globalDoc={globalDoc}
        model={model}
        params={params}
        position={position}
        plane={plane}
        planeValue={planeValue}
        planesOnCabinet={planesCab}
        internalWalls={[]}
        awDims={awDims}
        bladeOrientations={bladeOrientations}
        smartlist={smartlist}
        // ---- COMBO LOUVER (KA-5245) — A1.1 render props ----
        // The template's :361-372 render data: `combo_mode`,
        // `combo_autosize_enabled`, `combo_faces_view`,
        // `combo_louver_selected_count` / `_total_count`, `enclosure_materials`.
        comboMode={comboMode.current}
        comboAutoSizeEnabled={comboAutoSizeEnabled.current}
        comboFacesView={
          accessoryType === 'louver'
            ? getComboFacesView(comboFaces.current, comboAwDims.current, comboAutoSizeEnabled.current)
            : []
        }
        comboCounts={comboMode.current ? comboLouverCounts(cms, params) : { count: 0, total: 0 }}
        enclosureMaterials={enclosureMaterials ?? []}
        // ---- A1.2/A1.3 wiring ----
        onComboAutoSizeToggle={onComboAutoSizeToggle}
        onComboSmartlistToggle={onComboSmartlistToggle}
        // ---- A1.4/A1.5 wiring ----
        onComboFaceToggle={onComboFaceToggle}
        onComboFaceField={onComboFaceField}
        onComboReset={onComboReset}
        onComboHowTo={onComboHowTo}
        onModelField={onModelField}
        onParamsField={onParamsField}
        onPositionField={onPositionField}
        onAutoSize={onAutoSize}
        paramsCascade={paramsCascade}
        onSmartlistToggle={() => {
          // Legacy `showSmartlist` (accessory.selector.js:3274-3284) does TWO
          // things: it flips `data-tab`, and — only when OPENING — it calls
          // `loadSmartlist()`. React was doing just the flip, so the panel
          // opened showing "No results" (measured: legacy 50 rows vs React 0,
          // with the badge still reading 62/5813086) until some unrelated
          // params edit happened to refill it. Same failure class as the
          // press_english classes shipped without their handlers: the control
          // was rendered, its behaviour was not.
          //
          // ★ ORDER MATTERS, and so does using the REF. `setTab` writes
          // `dataTabRef` synchronously — legacy's `$wrapper.attr('data-tab', …)`
          // — so the `loadSmartlist()` below is the one legacy makes, with the
          // panel already marked open. Reading the `dataTab` STATE here instead
          // (and letting the debounce read it too) is what left the panel empty.
          const next = dataTabRef.current === 'smartlist' ? 'mounting' : 'smartlist';
          setTab(next);
          if (next === 'smartlist') loadSmartlist();
        }}
        onSmartlistRow={(id) => {
          const current = stagingModel.current?.get(`master_${accessoryType}_id`);
          if (id === current) {
            setTab('ahu');
            return;
          }
          void applySmartlistItem(id, smartlist.items);
          setImgComponent();
          // ★ CLOSE FIRST, THEN reload — legacy's order. `selectFromSmartlist`
          // (:3310-3339) reaches `self.loadSmartlist()` with `data-tab` already
          // off 'smartlist': `_applySmartlistItem` flips it to 'mounting'
          // synchronously before its first `await` (:3302-3304). So legacy's
          // post-pick reload fetches COUNTS ONLY. With `dataTabRef` now driving
          // that decision, calling `loadSmartlist()` while the ref still said
          // 'smartlist' would add an items GET legacy never makes.
          setTab('ahu');
          loadSmartlist();
        }}
        onSmartlistReset={() => {
          const m = stagingModel.current;
          if (!m) return;
          const obj: Record<string, unknown> = {};
          if (accessoryType === 'damper') obj.blade_orientation_id = 1;
          m.set({ [`${accessoryType}_params`]: JSON.stringify(obj), [`master_${accessoryType}_id`]: null });
          markChanged();
          bump();
          loadSmartlist();
          updateCanvasPreview();
        }}
      />

      {/* ===== RIGHT PANEL: data-tab AHU / Mounting / Component / Smartlist ===== */}
      <div className="mounting_popup_wrapper" data-zoom={dataZoom} data-tab={dataTab}>
        <div className="mounting_popup_tabs">
          <button className="mounting_popup_tab" data-tab="ahu" onClick={() => setTab('ahu')}>
            AHU
          </button>
          <button
            className="mounting_popup_tab"
            data-tab="mounting"
            onClick={() => setTab('mounting')}
          >
            Mounting
          </button>
          {(accessoryType === 'damper' || accessoryType === 'louver' || accessoryType === 'hood') && (
            <button
              className="mounting_popup_tab"
              data-tab="component"
              onClick={() => setTab('component')}
            >
              {accessoryType === 'damper' ? 'Damper' : accessoryType === 'louver' ? 'Louver' : 'Hood'}
            </button>
          )}
        </div>
        <div className="zoom-icons">
          <button
            id="btn_view_reset"
            className="cad-btn icon-reset ka-has-tooltip"
            // eslint-disable-next-line react/no-unknown-property
            {...{ tooltip: 'Default View' }}
            onClick={() => preview.resetView()}
          />
          <span
            className="material-symbols-outlined zoom-icon zoom-in ka-has-tooltip"
            {...{ tooltip: 'Zoom In' }}
            data-type="zoom-out"
            onClick={() => setDataZoom('zoom-out')}
          >
            &#xe56b;
          </span>
          <span
            className="material-symbols-outlined zoom-icon zoom-out ka-has-tooltip"
            {...{ tooltip: 'Zoom Out' }}
            data-type="zoom-in"
            onClick={() => setDataZoom('zoom-in')}
          >
            &#xeb2d;
          </span>
        </div>

        {/* ★ `.mounting_popup_content` — louver.selector.tmpl:644 /
            damper.selector.tmpl. ONLY those two legacy templates have it, and it
            is what STACKS the flip-card and the 3-D views on top of each other:
            `.mounting_popup_content { flex:1; min-height:0; position:relative }`
            with `> .mounting-flip-card` and `> .mounting_popup_views` both
            `position:absolute; inset:0` (rating_mode.scss:9800-9854), plus the
            `[data-tab="ahu"]` crossfade that hides the flip-card and shows the
            views. Without it the flip-card fell back to `flex:1` and
            `.mounting_popup_views` to `display:block !important`
            (:9757) — two 496px boxes stacked IN FLOW instead of one 572px box,
            which is the other half of the occlusion above. hood/opening have
            NO flip-card and NO content wrapper in legacy, so they take the
            `else` branch below and put the mounting host directly in the
            wrapper (hood.selector.tmpl:624, opening.selector.tmpl). */}
        {accessoryType === 'damper' || accessoryType === 'louver' ? (
        <div className="mounting_popup_content">
        <div className="mounting-flip-card">
          <div className="mounting-flip-inner">
            <div className="mounting-flip-front">
              <div ref={mountingHostRef} id={`${accessoryType}_mounting`} className="mounting_popup_view" />
              {(accessoryType === 'damper' || accessoryType === 'louver') && (
                <>
                  <img
                    className="mounting_popup_image"
                    src={imgState.src || `/static/css/common/images/${accessoryType}/${accessoryType}.avif`}
                    alt=""
                  />
                  <div className="mounting_popup_image_label">{imgState.label}</div>
                  <div className="mounting_popup_image_sublabel">(Typical model. Not scaled)</div>
                </>
              )}
            </div>
            {(accessoryType === 'damper' || accessoryType === 'louver') && (
              <div className="mounting-flip-back">
                {/* COMBO LOUVER — A1.2: in combo mode the flip-back IS the SHARED
                    combo smartlist (louver.selector.tmpl:653-692):
                     · `<span class="combo-smartlist-close">CLOSE</span>` (:655) —
                       note it lives OUTSIDE the dialog el, inside this popup, which
                       is why legacy binds it in the `events` hash rather than in
                       the dialog template;
                     · a DIFFERENT heading set — MANUFACTURER w-160 / SERIES w-100
                       / FRAME MATERIAL w-180 / BLADE w-120 (:660-666) — because a
                       combo row is a PRODUCT LINE, not a sized part: there are no
                       WIDTH/HEIGHT columns at all;
                     · rows come STATICALLY from `cms.combo_smartlist` (43 rows on
                       dev3), never from the per-part loader (Q12 — `loadSmartlist`
                       and friends all early-return on combo mode), so there is NO
                       infinite scroll here;
                     · `synced` when series_id AND frame_mtl_id both match params. */}
                <div className="mounting-smartlist-content">
                  {comboMode.current && (
                    <span
                      className="combo-smartlist-close"
                      title="Close"
                      onClick={() => setTab('ahu')}
                    >
                      CLOSE
                    </span>
                  )}
                  <div className="filter-list">
                    <div className="col-xs-12 padding-right-0">
                      <ul className="list-heading list-inline" ref={smartlistHeadingRef}>
                        <li className={`only-for-${accessoryType} custom-columns`}>
                          {comboMode.current ? (
                            <>
                              <h4 className="w-160">MANUFACTURER</h4>
                              <h4 className="w-100">SERIES</h4>
                              <h4 className="w-180">FRAME MATERIAL</h4>
                              <h4 className="w-120">BLADE</h4>
                            </>
                          ) : (
                            <>
                              <h4 className="w-200">MANUFACTURER</h4>
                              <h4 className="w-100">SERIES</h4>
                              {accessoryType === 'damper' && <h4 className="w-60">BLADES</h4>}
                              {/* w-70 on damper, legacy's w-80 elsewhere (QA, 2026-09-08).
                                  The louver smartlist was not part of that report, so it
                                  keeps `damper.selector.tmpl`'s sibling width. */}
                              <h4 className={`${whCol} text-align-right`}>WIDTH</h4>
                              <h4 className={`${whCol} text-align-right`}>HEIGHT</h4>
                              {/* FRAME — NOT a legacy column: damper.selector.tmpl:687-693
                                  stops at HEIGHT. Added 2026-09-08 on request, backed by
                                  `frame_mtl_name`, newly emitted by
                                  MasterCMSAHUDamperSerializer. Damper only — the louver
                                  smartlist has no frame material of its own.

                                  It takes the six columns past the panel's width, which
                                  the panel is NOT widened for (QA, 2026-09-08: "Don't
                                  increase the Smart List width. If necessary, add
                                  horizontal scrolling instead"). The list scrolls
                                  sideways instead — see `onSmartlistScroll` and
                                  ahuBoardOverrides.scss. */}
                              {accessoryType === 'damper' && <h4 className="smartlist-frame-col">FRAME</h4>}
                            </>
                          )}
                        </li>
                      </ul>
                      {comboMode.current ? (
                        <ComboSmartlistRows
                          items={comboSmartlistFilteredItems(cms, params)}
                          params={params}
                          onRow={onComboSmartlistRow}
                          onScroll={onSmartlistScroll}
                        />
                      ) : (
                        <SmartlistRows
                          type={accessoryType}
                          smartlist={smartlist}
                          selectedId={model[`master_${accessoryType}_id`]}
                          onScroll={onSmartlistScroll}
                          onRow={(id) => {
                            const current = stagingModel.current?.get(`master_${accessoryType}_id`);
                            if (id === current) {
                              setTab('ahu');
                              return;
                            }
                            void applySmartlistItem(id, smartlist.items);
                            setImgComponent();
                            // close-then-reload — legacy's order, see the twin
                            // handler passed as `onSmartlistRow` above.
                            setTab('ahu');
                            loadSmartlist();
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AHU mini-3D canvas host */}
        <div className="mounting_popup_views">
          <div ref={setAhuHost} className="mounting_popup_ahu_view" />
          <span className="accessory-tooltip-for-canvas hide" />
        </div>
        </div>
        ) : (
          /* hood / opening — legacy has no flip-card and no
             `.mounting_popup_content`: the mounting host is a direct child of
             the wrapper (`display:none !important` while data-tab="ahu",
             `height:344px !important` at zoom-in) and `.mounting_popup_views`
             is the flex:1 child that the 3-D canvas fills. */
          <>
            <div ref={mountingHostRef} id={`${accessoryType}_mounting`} className="mounting_popup_view" />
            {accessoryType === 'hood' && (
              <>
                <div className="mounting_popup_image mounting_popup_hood_image">
                  {hoodStyleValue && <span className={`icon-hood icon-${hoodStyleValue}`} />}
                </div>
                {hoodStyleName && (
                  <>
                    <div className="mounting_popup_image_label">{hoodStyleName}</div>
                    <div className="mounting_popup_image_sublabel">(Typical model. Not scaled)</div>
                  </>
                )}
              </>
            )}
            <div className="mounting_popup_views">
              <div ref={setAhuHost} className="mounting_popup_ahu_view" />
              <span className="accessory-tooltip-for-canvas hide" />
            </div>
          </>
        )}
      </div>

      {/* COMBO LOUVER — A1.5: the *How to* popup. ★ It is `createPortal`'d to
          `#ksdm-content` INSIDE the component (see ComboLouverHowTo) because
          `.floating-popup` is `position: fixed` and `#component_rating_mode` /
          `.rotate-card` carry the board's card-flip `transform`, which would
          become its containing block — the same reason legacy appends it there
          (:3382) and the same fix the filter + fan how-tos already use. */}
      {comboMode.current && howToOpen && <ComboLouverHowTo onClose={() => setHowToOpen(false)} />}

      {/* the reused base mounting popup (inlineContainer mode) into the host */}
      {mountingApi && dataTab === 'mounting' && (
        <AccessoryMountingPopup
          api={mountingApi}
          typeName={accessoryType}
          typeValue={accessoryType}
          inlineContainer={mountingHostRef.current}
        />
      )}
    </div>
  );
};

export default AccessorySelector;
